import { useReactiveVar } from '@apollo/client'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { debounce } from '@deltek/specification-client/core/Utilities/formUtils'
import {
    decodeEntities,
    encodeEntities,
} from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Input } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'

import { DTVName } from 'api/graphQL/reactiveVars/element'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    moveUpAndDown,
    newlyAddedParagraphText,
    promoteDemoteData,
    promoteDemoteTriggered,
    reactiveWorkspaceActions,
    setMoveUpAndDownTrigger,
    setPromoteDemoteContent,
    setPromoteDemoteTriggered,
    setPromoteDemoteWorkspaceTemplateData,
    setReactiveWorkspaceActions,
    setWorkspaceTemplateData,
    templateData,
    templateFlags,
} from 'api/graphQL/reactiveVars/workspaceActions'
import {
    isContentTeam,
    isOmWorkspaceUser,
    isReactiveContentTeamFromStudioPage,
    setAddQueueSib,
    setIsAddOnQueue,
    setIsAddOngoing,
    updateLastElementIndexId,
    workspaceContents,
    workspaceHasError,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import clsx from 'clsx'
import { TrackChangeType } from 'models/changes'
import {
    defaultOOFColor,
    manufacturerColor,
    manufacturerSecondaryColor,
    manufacturerSecondaryColorOOFColor,
} from 'pages/ProjectsPage/colorVars'
import AddProductType from 'pages/ProjectsPage/components/AddProductType/AddProductType'
import AddSustainabilityTopic from 'pages/ProjectsPage/components/AddSustainabilityTopic/AddSustainabilityTopic'
import React, { useCallback, useContext } from 'react'
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable'
import sanitizeHtml from 'sanitize-html'
import { getArticles } from 'shared/utils/workspaceUtils'
import { AreaContext } from '../Common/AreaContext'
import {
    articleDefaultValue,
    getPropertyDefaultValue,
} from '../Common/addUtils'
import { InsertUOMPillProps, useInsertPill } from '../Common/hooks'
import {
    NormalizedDocumentTemplateViewItem,
    PromoteDemoteRequirements,
} from '../Common/model'
import { tryHandleHtmlParagraphPaste } from '../Common/pasteUtils'
import {
    ActionButtonVariant,
    PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING as ShortcutKeys,
    PROPERTY_EDITOR_TEXT_FORMAT_TYPE as TextFormatType,
    TextVariant,
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as TextVariants,
} from '../constants'
import DeletePills from './DeletePills'
import {
    areEq,
    containsBlockedCharacters,
    haltEvent,
    insertPillTextUOM,
    sanitizeZeroWidthText,
    shouldHaltKeyboardEvent,
    tryGetShortcutKey,
} from './propertyEditorUtils'

const useStyles = makeStyles((theme: Theme) => ({
    editableText: {
        padding: theme.spacing(0.375, 0.75),
        wordBreak: 'break-word',
        position: 'relative',
        whiteSpace: 'pre-wrap',
    },
    notEditableText: {
        color: '#40464F',
        // In some other browsers like Firefox might still show the text cursor (I-beam pointer) when hovering over a contentEditable="false" element, giving the impression that it’s editable.
        // explicitly setting cursor to "default" to not confuse user and do not allow user-select/highlighting to be more consistent
        cursor: 'default',
        userSelect: 'none',
    },
    ptGroupText: { flex: 1, width: '100%' },

    hasTitle: {
        '&[data-title]:not([data-title=""])': {
            '&:before': {
                content: 'attr(data-title)',
                color: (props: PropertyEditorEditableTextStyleProps) =>
                    props.editable
                        ? props.isElementDeleted
                            ? '#dadada'
                            : theme.palette.common.black
                        : '#40464f',
                fontSize: pxToRem(14),
                float: 'left',
                display: (props: PropertyEditorEditableTextStyleProps) =>
                    props.isElementDeleted ? 'inline-block' : 'contents',
                fontStyle: (props: PropertyEditorEditableTextStyleProps) =>
                    props.isElementDeleted ? 'italic' : 'unset',
                textDecoration: (props: PropertyEditorEditableTextStyleProps) =>
                    props.isElementDeleted ? 'line-through' : 'unset',
                textDecorationColor: (
                    props: PropertyEditorEditableTextStyleProps
                ) => (props.isElementDeleted ? '#FF6060' : 'unset'),
                textDecorationThickness: (
                    props: PropertyEditorEditableTextStyleProps
                ) => (props.isElementDeleted ? '1px' : 'unset'),
            },
        },
    },
    nonEditableFocusableArticle: {
        width: '100%',
    },

    close: {
        border: 'black 2px solid',
        borderRadius: '53%',
        marginLeft: 10,
        position: 'relative',
        width: 16,
        left: 4,
        height: 16,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 'bold',
    },
    textInUom: {
        width: '20px',
        color: '#FF6060',
        display: 'inline-block',
    },
    closeActive: {
        border: '#F78909 2px solid',
        borderRadius: '53%',
        marginLeft: 10,
        position: 'relative',
        width: 16,
        left: 4,
        height: 16,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 'bold',
        color: '#F78909',
        cursor: 'pointer',
    },
    editableTextPlain: {
        outline: 'none',
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },
    nonEmptyPlainTextEditor: {
        display: 'inline',
    },
    emptyPlainTextEditor: {
        display: 'inline-block',
        margin: 0,
        maxHeight: 20,
        padding: 0,
        minWidth: pxToRem(1),
    },
    addedPillInUOM: {
        minWidth: pxToRem(25),
        padding: theme.spacing(0, 1),
        border: '1px solid',
    },
    addedPillInUOMText: {
        minWidth: pxToRem(25),
        display: 'inline-block',
    },
    editableTextBoxed: {
        background: '#FFF9F2',
        borderRadius: pxToRem(4),
        lineHeight: '16px',
        minHeight: 25,
        outline: 'none',
        display: 'inline-block',
        paddingTop: theme.spacing(0.375),
    },
    disabledBoxedInputContainer: {
        color: '#40464F',
        border: '1px solid #40464F',
        background: theme.palette.common.white,
    },
    notDisabledBoxedInputContainer: {
        border: '1px solid #F78909',
        color: theme.palette.common.black,
        margin: (props: PropertyEditorEditableTextStyleProps) =>
            props.calledFromImportPage || props.editable
                ? theme.spacing(0.5, 0.25)
                : 'inherit',
    },
    emptyEditableTextBox: {
        display: 'inline-block',
    },
    editableNewlyAdded: {
        outline: 'none',
        padding: IsFireFox
            ? theme.spacing(0, 0, 0, 0)
            : theme.spacing(0, 0, 0.12, 0),
        color: '#FF6060',
        fontStyle: 'italic',
        fontWeight: 400,
        fontSize: pxToRem(14),
    },
    hasNoErrors: {
        border: '1px solid #F78909',
        background: theme.palette.common.white,
    },
    hasError: {
        border: '1px solid red',
        background: '#FFF5F5',
    },
    borderLessHasError: {
        borderBottom: '1px solid red',
    },
    actualBoxedEditor: {
        outline: 'none',
        minWidth: 8,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        position: 'relative',
        marginBottom: theme.spacing(-0.375),
    },
    endIcon: {
        marginLeft: theme.spacing(0.5),
    },
    nonEmptyActualBoxedEditor: {
        display: IsFireFox ? 'inline' : 'inline-flex',
    },
    nonEmptyPlainTextEditorContainer: {
        display: 'inline-flex',
        alignItems: 'baseline',
        position: 'relative',
        bottom: theme.spacing(-0.125),
    },
    newlyAddedContainer: {
        width: 'inherit',
        padding: 0,
        alignItems: 'center',
    },
    addedElementText: {
        color: '#FF6060',
    },
    addedBoxedElement: {
        color: '#FF6060',
        border: '1px solid #FF6060',
    },
    addedTextInUOMPillElement: {
        border: 'none',
    },
    deletedBoxedElement: {
        color: '#a19d9d',
        border: '1px solid #FF6060',
        margin: theme.spacing(0, 0.5),
        whiteSpace: 'nowrap',
    },
    deletedText: {
        fontStyle: 'italic',
        textDecoration: 'line-through',
        textDecorationColor: '#FF6060',
        textDecorationThickness: '0px', // 0px to make it look like a normal line-through and consistent
        color: '#dadada',
    },
    articleText: {
        fontWeight: 400,
        fontStyle: (props: PropertyEditorEditableTextStyleProps) =>
            props.isNewlyAddedArticle ? 'italic' : 'normal',
        color: (props: PropertyEditorEditableTextStyleProps) => {
            return props.disableArticleExpandInFullView
                ? defaultOOFColor
                : props?.isHidden
                ? props?.isPremiumPlus
                    ? manufacturerSecondaryColorOOFColor
                    : '#A7896640'
                : (!!props?.changeType &&
                      props.changeType.toUpperCase() !==
                          TrackChangeType.ACCEPTED &&
                      props.isTrackChangeToggleOn) ||
                  props.isNewlyAddedArticle
                ? '#FF6060'
                : props?.isPremiumPlus
                ? manufacturerSecondaryColor
                : '#A78966'
        },
        fontSize: pxToRem(18),
        textDecoration: (props: PropertyEditorEditableTextStyleProps) =>
            !!props?.changeType &&
            props.changeType.toUpperCase() === TrackChangeType.DELETED &&
            props.isTrackChangeToggleOn
                ? 'line-through'
                : 'unset',
    },
    deletedBoxedText: {
        color: '#8e8f91',
    },
    unselectedOptionItem: {
        padding: theme.spacing(0.25, 1),
        color: '#FF6060',
        background: '#FFF9F2',
        display: 'inline-block',
        border: '1px solid #FF6060',
        borderRadius: pxToRem(20),
        textAlign: 'left',
        top: '2px',
        lineBreak: 'anywhere',
        minWidth: '100px',
        wordBreak: 'break-word',
        lineHeight: pxToRem(20),
    },
    unselectedOptionItempill: {
        padding: theme.spacing(0.25, 1),
        color: '#FF6060',
        background: '#FFF9F2',
        display: 'inline-block',
        border: '1px solid #FF6060',
        borderRadius: pxToRem(20),
        textAlign: 'left',
        top: '2px',
        lineBreak: 'anywhere',
        wordBreak: 'break-word',
        lineHeight: pxToRem(20),
    },
    unselectedOption: {
        padding: theme.spacing(0.25, 1),
        color: '#FF6060',
        background: '#FFF9F2',
        display: 'inline-block',
        border: '1px solid #FF6060',
        lineHeight: pxToRem(0.8),
        borderRadius: pxToRem(5),
        textAlign: 'left',
        width: '10rem',
        height: '3rem',
        top: '2px',
    },
    unselected: {
        padding: theme.spacing(0.25, 1),
        color: '#FF6060',
        background: '#FFF9F2',
        display: 'inline-block',
        lineHeight: pxToRem(0.8),
        textAlign: 'left',
        width: '5rem',
        height: '3rem',
        top: '2px',
    },
    manufacturerUnits: {
        borderColor: manufacturerColor,
        color: manufacturerColor,
        margin: theme.spacing(0, 0.5),
    },
    strikeout: {
        position: 'absolute',
        border: '1px solid #FF6060',
        borderBottom: 0,
        left: `-${pxToRem(10)}`, // This is needed to be negative so that the strikeout fall over the element
        cursor: 'pointer',
        userSelect: 'none',
    },
}))

const IsFireFox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1

export interface PropertyEditorEditableTextProps
    extends PropertyEditorEditableTextStyleProps {
    element?: Partial<TextContentElement> | undefined
    fieldState?: Partial<TextContentElement>
    value?: string
    Uomcreate?: boolean
    onRightClick?: (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        elementId: string | undefined,
        isBestPractice: boolean,
        isGlobalBestPractice: boolean
    ) => void
    elementId?: string
    isBestPractice?: boolean
    isGlobalBestPractice?: boolean
    isBestPracticeViewDisabled?: boolean
    isBestPracticeEditDisabled?: boolean
    isDeleteFormatted?: boolean
    onChange?: (newValue: string, type?: string, index?: number) => void
    variant?: TextVariant
    additionalBlockedCharacters?: string[]
    editorRef?: Function | React.RefObject<HTMLElement> | HTMLElement
    hasErrors?: boolean
    setHasErrors?: any
    identifier?: string
    titleText?: string
    maxChars?: number
    editable?: boolean
    fieldStateType?: string
    bodId?: string
    hideCustomManufacturer?: boolean
    isUserAdded: boolean
    changeType?: TrackChangeType
    isOverwrite?: boolean
    onDeleteOption?: () => void
    insertUomPill?: InsertUOMPillProps
    saveChanges?: (
        updatedElementList: TextContentElement[],
        nonFormattedText: string,
        indexOfPillForNewPt?: number,
        typeOfPillForNewPt?: string,
        isPillWithNewPara?: boolean
    ) => void
    contentTeamFromStudioPage?: boolean
    showMetric?: boolean
    showImperial?: boolean
    onBlurInput?: (
        value?: string,
        type?: string,
        event?: React.FocusEvent<
            HTMLTextAreaElement | HTMLInputElement | HTMLSpanElement
        >
    ) => void
    handleChangepillInUom?: (
        event: ContentEditableEvent,
        type?: string,
        index?: number
    ) => void
    isToUpdateElement?: boolean
    template?: NormalizedDocumentTemplateViewItem
    setPromoteDemoteRequirements?: (input?: PromoteDemoteRequirements) => void
    triggerAddNewProperty?: () => void
    setProperty?: (input?: any) => void
    setFieldState?: (input?: any) => void
    /** Called when the user tries to paste HTML paragraphs
     * (e.g., copied from Word) into the edit box */
    onHtmlParagraphPaste?: (htmlBeingPasted: string) => void
    focusState?: boolean

    // props from Article.tsx
    isNewlyAddedArticle?: boolean
    disableArticleExpandInFullView?: boolean
    isHidden?: boolean
    onCancelAddProperty?: () => void
    displayPillDeleteDialog?: (showModal: boolean) => void
    parentTemplate?: NormalizedDocumentTemplateViewItem
    productTypeInFamilyViewContentType?: boolean
    isNotFromContentTeam?: boolean
}

export interface PropertyEditorEditableTextStyleProps {
    editable?: boolean
    calledFromImportPage?: boolean
    isElementDeleted?: boolean
    isHidden?: boolean
    isNewlyAddedArticle?: boolean
    isPremiumPlus?: boolean
    changeType?: TrackChangeType
    isTrackChangeToggleOn?: boolean
    disableArticleExpandInFullView?: boolean
}

export interface PrePasteSnapshotInfo {
    value: string
    caretPosition: number
}

const emptyDiv =
    '<div id="textToBeAdded" data-testid="textToBeAdded"  contenteditable="true" style="color: #FF6060; display: inline-block;">&zwj;</div>'
const getDeletedDiv = (deletedText: string) => {
    return `<div id="textToBeDeleted" data-testid="textToBeDeleted" style="font-style:italic; text-decoration: line-through; text-decoration-color: #FF6060;text-decoration-thickness: 1px;color: #dadada; display:inline" contenteditable="false">${deletedText}</div>`
}
const getPermanentlyDeletedDiv = (deletedText: string) => {
    return `<div id="textToBePermanentlyDeleted" data-testid="textToBePermanentlyDeleted" style="display:none" contenteditable="false">${deletedText}</div>`
}

const getAddedDiv = (addedText: string) => {
    return `<div id="textToBeAdded" data-testid="textToBeAdded" style="color: #FF6060; display:inline" contenteditable="true">${addedText}</div>`
}
const emptySpace = ''
const textToBeAdded = 'textToBeAdded'
const textToBeDeleted = 'textToBeDeleted'
const textToBePermanentlyDeleted = 'textToBePermanentlyDeleted'

const maxTextLength = 1000

export const sanitizeAndDecodeValue = (val: string, shouldUseRawFallback = false) => {
    if (!val) return ''
    const sanitizedNewVal = sanitizeHtml(val, {
        allowedTags: [],
    })
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')

    // sanitize-html strips tag-shaped values like <textfield> to '' because they have no
    // inner text content. When shouldUseRawFallback is true (read-only display contexts only),
    // fall back to the raw value so textfield insertion markers display literally in React
    // text nodes (safe: rendered via JSX text, not dangerouslySetInnerHTML).
    // Do NOT pass shouldUseRawFallback=true when the value flows into ContentEditable html prop —
    // raw tag strings render as DOM elements and crash extractUpdatedElementNodes.
    if (shouldUseRawFallback && !sanitizedNewVal.trim() && val.trim()) {
        return decodeEntities(val)
    }

    return decodeEntities(sanitizedNewVal)
}

interface FormatDeleteInput {
    isDeleteKey: boolean
    isNewlyAdded: boolean
    currentText: string
    isToFormat: boolean
    isAtTheBeginning: boolean
}
interface FormatTextInput {
    eventElementNodes: any[]
    anchorIndex: number
    selection: Selection
    variant: keyof Pick<typeof TextVariants, 'plain' | 'boxed' | 'newlyadded'>
    startIndex: number
}
interface FormattedText {
    text: string
    caretPosition?: number
}
interface FormatReplaceInput {
    replacedWord?: string
    replacedBy: string
}

export const getNodeIndex = (eventNodes: Node[] | any[], node: Node) => {
    let nodePreviousSiblingIndex = eventNodes?.findIndex((x) =>
        x.isEqualNode(
            node?.parentNode?.previousSibling ?? node?.previousSibling
        )
    )

    if (nodePreviousSiblingIndex < 0) {
        nodePreviousSiblingIndex = 0
    }
    const nodeIndex = eventNodes
        .slice(nodePreviousSiblingIndex)
        .findIndex((childNode: any) => {
            return (
                childNode.isEqualNode(node) ||
                (childNode as any)?.isEqualNode(node?.parentNode)
            )
        })

    return nodePreviousSiblingIndex + nodeIndex
}

export const isFocusNodeDifferentFromAnchor = (
    focusNode: Node,
    anchorNode: Node,
    focusNodeIndex: number,
    anchorNodeIndex: number
) => {
    return (
        !anchorNode?.isEqualNode(focusNode) ||
        focusNodeIndex !== anchorNodeIndex
    )
}

export const getAnchorIndex = (
    eventNodes: Node[] | any[],
    selection: Selection
) => {
    const focusNodeIndex = getNodeIndex(eventNodes, selection?.focusNode!)
    const anchorNodeIndex = getNodeIndex(eventNodes, selection?.anchorNode!)

    if (
        isFocusNodeDifferentFromAnchor(
            selection?.focusNode!,
            selection?.anchorNode!,
            focusNodeIndex,
            anchorNodeIndex
        ) &&
        focusNodeIndex < anchorNodeIndex
    ) {
        return focusNodeIndex
    }
    return anchorNodeIndex
}

export const formatNewText = (
    formatType: keyof typeof TextFormatType,
    defaultInput: FormatTextInput,
    deleteInput?: FormatDeleteInput,
    replaceInput?: FormatReplaceInput,
    contentTeamFromStudioPage?: boolean
) => {
    const {
        eventElementNodes,
        anchorIndex,
        selection,
        variant,
        startIndex,
    } = defaultInput
    let newText = ''
    let wordInAction = ''
    let caretPosition = 0
    const focusNodeIndex = getNodeIndex(
        eventElementNodes,
        selection?.focusNode!
    )
    const anchorNodeIndex = getNodeIndex(
        eventElementNodes,
        selection?.anchorNode!
    )

    const areMultipleNodesInvolved = isFocusNodeDifferentFromAnchor(
        selection?.focusNode!,
        selection?.anchorNode!,
        focusNodeIndex,
        anchorNodeIndex
    )
    let updatedRangeOffset = (selection as any)?.extentOffset
    const deletedString = eventElementNodes
        ?.filter((x) => x?.id === textToBeDeleted)
        ?.reduce((deletedChar, char) => {
            return `${deletedChar}${char.outerText}`
        }, '')
    let isEverythingDeleted = false
    let isStillPartOfFocusNodes = false
    const rangeDetails = selection?.getRangeAt(0)
    let multipleNodeCharLeftCounter =
        rangeDetails?.cloneContents()?.textContent?.length ?? 0
    eventElementNodes.forEach((node, index) => {
        if (index < anchorIndex) {
            newText =
                node.nodeType === 3
                    ? `${newText}${node.textContent}`
                    : `${newText}${(node as any).outerHTML}`
        } else if (
            index === anchorIndex ||
            (areMultipleNodesInvolved && isStillPartOfFocusNodes)
        ) {
            const nodeFocusedTextLength = (
                node?.outerText ?? node?.textContent
            )?.substring(rangeDetails?.startOffset)?.length
            const actualRangeOffset = !areMultipleNodesInvolved
                ? !IsFireFox
                    ? (selection as any)?.extentOffset <
                      (selection as Selection).anchorOffset
                        ? selection?.anchorOffset
                        : (selection as any)?.extentOffset
                    : (selection as any)?.focusOffset <
                      (selection as Selection).anchorOffset
                    ? selection?.anchorOffset
                    : (selection as any)?.focusOffset
                : index === anchorIndex
                ? (node as any)?.id === textToBeAdded
                    ? nodeFocusedTextLength
                    : rangeDetails?.startOffset + nodeFocusedTextLength
                : updatedRangeOffset

            let isDeleteKey = false
            let isNewlyAdded = false

            if (deleteInput || (replaceInput && areMultipleNodesInvolved)) {
                isDeleteKey = deleteInput?.isDeleteKey ?? false
                isNewlyAdded =
                    ((deleteInput?.isNewlyAdded ||
                        (node as any)?.id === textToBeAdded) &&
                        !areMultipleNodesInvolved) ||
                    (areMultipleNodesInvolved &&
                        (node as any)?.id === textToBeAdded)
            }
            const isRange = selection?.type === 'Range'
            const textStartIndex =
                isRange ||
                isDeleteKey ||
                (isNewlyAdded && selection?.anchorNode?.nodeType === 1) ||
                formatType === TextFormatType.replace
                    ? startIndex
                    : startIndex - 1
            caretPosition = textStartIndex
            const preUnmodifiedText =
                areMultipleNodesInvolved &&
                (((node as any)?.id === textToBeAdded &&
                    node.textContent.length === actualRangeOffset) ||
                    isStillPartOfFocusNodes)
                    ? ''
                    : node.textContent?.substring(0, textStartIndex)
            const postUnmodifiedText =
                areMultipleNodesInvolved &&
                (node as any)?.id === textToBeAdded &&
                ((!node.textContent?.includes(' ') && index !== anchorIndex) ||
                    index === anchorIndex)
                    ? ''
                    : node.textContent?.substring(
                          isRange
                              ? actualRangeOffset
                              : isDeleteKey
                              ? (selection?.anchorOffset as number) + 1
                              : (selection?.anchorOffset as number)
                      )

            wordInAction =
                selection?.type === 'Range'
                    ? areMultipleNodesInvolved &&
                      node.textContent.length !== actualRangeOffset
                        ? node.textContent?.substring(
                              0,
                              multipleNodeCharLeftCounter
                          )
                        : startIndex > actualRangeOffset ||
                          (areMultipleNodesInvolved && index === focusNodeIndex)
                        ? node.textContent
                        : node.textContent?.substring(
                              startIndex,
                              actualRangeOffset
                          )
                    : isDeleteKey
                    ? node.textContent?.charAt(startIndex)
                    : node.textContent?.charAt(startIndex - 1)

            if (areMultipleNodesInvolved) {
                multipleNodeCharLeftCounter =
                    multipleNodeCharLeftCounter! -
                    (wordInAction.length > 0
                        ? wordInAction.length
                        : node.textContent.length)
                isStillPartOfFocusNodes = multipleNodeCharLeftCounter > 0
                updatedRangeOffset = multipleNodeCharLeftCounter
                if (
                    ((isNewlyAdded &&
                        eventElementNodes[index + 1].textContent === ' ') ||
                        (!isNewlyAdded && wordInAction === ' ')) &&
                    wordInAction.length < node.textContent.length
                ) {
                    multipleNodeCharLeftCounter =
                        eventElementNodes[index + 1].textContent.length
                }
            }
            if (isNewlyAdded) {
                wordInAction = ''
            }
            if (
                variant === TextVariants.boxed &&
                formatType !== TextFormatType.delete
            ) {
                newText = `${newText}${node.textContent}`
            } else {
                if (formatType === TextFormatType.delete && deleteInput) {
                    const { isToFormat, isAtTheBeginning } = deleteInput
                    isEverythingDeleted =
                        wordInAction === deleteInput?.currentText ||
                        `${wordInAction}${deletedString}` ===
                            deleteInput?.currentText
                    if (isToFormat) {
                        newText = isEverythingDeleted
                            ? `${getDeletedDiv(wordInAction)}`
                            : !isNewlyAdded
                            ? `${newText}${preUnmodifiedText}${getDeletedDiv(
                                  wordInAction
                              )}${postUnmodifiedText}`
                            : `${newText}${
                                  !!preUnmodifiedText || !!postUnmodifiedText
                                      ? getAddedDiv(
                                            `${preUnmodifiedText}${postUnmodifiedText}`
                                        )
                                      : ''
                              }`
                        if (isAtTheBeginning) {
                            newText = `${getAddedDiv('\u200d')}${newText}`
                            caretPosition = 1
                        }
                    } else {
                        newText = isEverythingDeleted
                            ? `${getPermanentlyDeletedDiv(wordInAction)}`
                            : !isNewlyAdded
                            ? `${newText}${preUnmodifiedText}${getPermanentlyDeletedDiv(
                                  wordInAction
                              )}${postUnmodifiedText}`
                            : `${newText}${
                                  !!preUnmodifiedText || !!postUnmodifiedText
                                      ? getAddedDiv(
                                            `${preUnmodifiedText}${postUnmodifiedText}`
                                        )
                                      : ''
                              }`
                    }
                } else if (
                    formatType === TextFormatType.replace &&
                    replaceInput
                ) {
                    const { replacedBy, replacedWord } = replaceInput
                    if (!areMultipleNodesInvolved) {
                        newText = `${newText}${preUnmodifiedText}${replacedWord}${replacedBy}${postUnmodifiedText}`
                    } else {
                        if (isNewlyAdded) {
                            newText = isStillPartOfFocusNodes
                                ? `${newText}${preUnmodifiedText}${postUnmodifiedText}`
                                : `${newText}${preUnmodifiedText}${replacedBy}${postUnmodifiedText}`
                        } else {
                            newText = isStillPartOfFocusNodes
                                ? `${newText}${preUnmodifiedText}${getDeletedDiv(
                                      wordInAction
                                  )}${postUnmodifiedText}`
                                : `${newText}${preUnmodifiedText}${getDeletedDiv(
                                      wordInAction
                                  )}${replacedBy}${postUnmodifiedText}`
                        }
                    }
                } else if (formatType === TextFormatType.add) {
                    if (contentTeamFromStudioPage) {
                        newText = `${newText}${preUnmodifiedText}${getAddedDiv(
                            wordInAction
                        )}${postUnmodifiedText}`
                    } else {
                        newText =
                            wordInAction !== ':'
                                ? `${newText}${preUnmodifiedText}${getAddedDiv(
                                      wordInAction
                                  )}${postUnmodifiedText}`
                                : `${newText}${node.textContent}`
                    }
                }
            }
        } else {
            newText =
                node.nodeType === 3
                    ? `${newText}${node.textContent}`
                    : `${newText}${(node as any).outerHTML}`
        }
    })

    return deleteInput
        ? {
              text: isEverythingDeleted ? `${newText}${emptyDiv}` : newText,
              caretPosition,
          }
        : { text: newText }
}

/**
 * This function extracts and updates the element nodes based on the given parameters.
 * It handles different scenarios such as adding, deleting, and concatenating text content elements.
 *
 * @param eventElementNodes - The original array of element nodes.
 * @param element - The base element to be used for updating the nodes.
 * @param changeType - The type of change (added, deleted, etc.).
 * @param titleText - The title text to be added at the beginning of the nodes.
 * @param isNotFromContentTeam - Indicates whether the update is not from the content team.
 * @param isUserAdded - Indicates whether the element is user-added.
 *
 * @returns An array of updated element nodes.
 */
export const extractUpdatedElementNodes = (
    eventElementNodes: any[],
    element: Partial<TextContentElement>,
    changeType?: TrackChangeType,
    titleText?: string,
    isNotFromContentTeam?: boolean,
    isUserAdded?: boolean
) => {
    let completeElementNodes = []

    if (
        titleText?.includes(':') &&
        eventElementNodes.some(
            (x) => x.id === textToBeAdded && x.textContent?.indexOf(':') === -1
        ) &&
        isNotFromContentTeam === true &&
        isUserAdded === false
    ) {
        const elementNodes = eventElementNodes
            .filter(
                (elementNode) =>
                    typeof elementNode?.textContent === 'string' &&
                    elementNode?.textContent.trim() !== '' &&
                    !/\u200d/.test(elementNode?.textContent)
            )
            .map((elementNode) => {
                if (elementNode.id === textToBeAdded) {
                    return {
                        changeType: 'ADDED',
                        id: element?.id,
                        isPermanentlyDeleted:
                            elementNode?.id === textToBePermanentlyDeleted,
                        text: elementNode?.textContent,
                        type: 'Text',
                    }
                } else if (
                    elementNode.id === textToBeDeleted ||
                    elementNode.id === textToBePermanentlyDeleted
                ) {
                    return {
                        changeType: 'DELETED',
                        id: element?.id,
                        isPermanentlyDeleted:
                            elementNode?.id === textToBePermanentlyDeleted,
                        text: elementNode?.textContent,
                        type: 'Text',
                    }
                } else {
                    return {
                        ...element,
                        text: elementNode?.textContent,
                    }
                }
            })

        completeElementNodes = [
            { ...element, text: titleText },
            ...elementNodes,
        ]
    } else {
        completeElementNodes = eventElementNodes
            ?.filter((x) => {
                return (
                    x?.textContent !== '' &&
                    !(x?.textContent as string)?.match(/\u200d/)
                )
            })
            .reduce((prevElementNodes, elementNode, index, filteredArray) => {
                elementNode.textContent =
                    elementNode.textContent.split('\xa0\xa0\xa0')[1] ??
                    elementNode.textContent
                const isDeletedNode =
                    (elementNode as any)?.id === textToBeDeleted
                const isPermanentlyDeleted =
                    (elementNode as any)?.id === textToBePermanentlyDeleted
                const isNewlyAdded = (elementNode as any)?.id === textToBeAdded
                const defaultValues = {
                    id: element.id,
                    text:
                        titleText && titleText !== '' && index === 0
                            ? `${titleText}${elementNode.textContent}`
                            : `${elementNode.textContent}`,
                    type: 'Text',
                }
                const previousElementNode = filteredArray?.[index - 1] as any

                //concat deleted text/different nodes
                if (
                    (isDeletedNode &&
                        previousElementNode?.id === textToBeDeleted) ||
                    (isPermanentlyDeleted &&
                        previousElementNode?.id === textToBePermanentlyDeleted)
                ) {
                    const prevDelete: Partial<TextContentElement> =
                        prevElementNodes?.[prevElementNodes?.length - 1]
                    return [
                        ...prevElementNodes.slice(
                            0,
                            prevElementNodes?.length - 1
                        ),
                        {
                            ...defaultValues,
                            text: `${prevDelete.text}${defaultValues.text}`,
                            changeType: 'DELETED',
                            isPermanentlyDeleted: isPermanentlyDeleted,
                        } as Partial<TextContentElement>,
                    ]
                } else {
                    const prevTextContentElement =
                        prevElementNodes?.[prevElementNodes?.length - 1]
                    const isToBeConcatenated =
                        prevTextContentElement?.changeType === 'DELETED' &&
                        prevTextContentElement?.isPermanentlyDeleted
                    if (isToBeConcatenated && defaultValues?.text !== '') {
                        const toMergeTextContentElement =
                            prevElementNodes?.[prevElementNodes?.length - 2]
                        return [
                            ...prevElementNodes.slice(
                                0,
                                prevElementNodes?.length - 2
                            ),
                            {
                                ...defaultValues,
                                ...toMergeTextContentElement,
                                text: `${toMergeTextContentElement.text}${defaultValues.text}`,
                                changeType:
                                    toMergeTextContentElement.changeType,
                            } as Partial<TextContentElement>,
                            {
                                ...prevTextContentElement,
                            },
                        ]
                    } else {
                        return prevElementNodes.concat([
                            {
                                ...(!(
                                    isDeletedNode ||
                                    isPermanentlyDeleted ||
                                    isNewlyAdded
                                ) && element),
                                ...defaultValues,
                                isPermanentlyDeleted: isPermanentlyDeleted,
                                changeType: isNewlyAdded
                                    ? 'ADDED'
                                    : isDeletedNode || isPermanentlyDeleted
                                    ? 'DELETED'
                                    : changeType,
                            } as Partial<TextContentElement>,
                        ])
                    }
                }
            }, [])
    }

    if (changeType?.toLowerCase() === TrackChangeType.DELETED.toLowerCase()) {
        completeElementNodes = [element].concat(completeElementNodes)
    }

    if (
        completeElementNodes[0].id === '' &&
        completeElementNodes[1].id === ''
    ) {
        const index = completeElementNodes[0].changeType === 'ADDED' ? 1 : 0
        completeElementNodes.splice(index, 1)
    }
    return completeElementNodes
}

/**
 *  This is a drop-in replacement for React.useCallback
 *  that forces react-contenteditable component to re-render
 *  the base component when the callbacks are updated due
 *  to dependency changes, thus avoiding stale closures from
 *  the previous render.
 *
 *  This is a workaround for the library's known compatibility
 *  issue with React hooks. More can be found here:
 *
 *  https://github.com/lovasoa/react-contenteditable/issues/161
 */
export const useRefCallback = <T extends any[]>(
    value: ((...args: T) => void) | undefined,
    deps?: React.DependencyList
): ((...args: T) => void) => {
    const ref = React.useRef(value)

    React.useEffect(() => {
        ref.current = value
    }, deps ?? [value]) // eslint-disable-line

    const result = React.useCallback((...args: T) => {
        ref.current?.(...args)
    }, [])

    return result
}

const PropertyEditorEditableText: React.FC<PropertyEditorEditableTextProps> = ({
    value = '',
    fieldState,
    variant = 'plain',
    onChange,
    onDeleteOption,
    isBestPracticeEditDisabled,
    onRightClick,
    elementId,
    isBestPractice,
    isGlobalBestPractice,
    insertUomPill,
    handleChangepillInUom,
    Uomcreate,
    additionalBlockedCharacters = [],
    editorRef,
    hasErrors = false,
    setHasErrors,
    identifier,
    titleText,
    maxChars = maxTextLength,
    editable = true,
    fieldStateType,
    bodId,
    hideCustomManufacturer,
    isUserAdded,
    calledFromImportPage,
    changeType,
    isOverwrite,
    element,
    isDeleteFormatted,
    isToUpdateElement,
    contentTeamFromStudioPage,
    onBlurInput,
    triggerAddNewProperty,
    onHtmlParagraphPaste,
    saveChanges,
    template,
    setPromoteDemoteRequirements,
    focusState,
    showImperial,
    showMetric,
    disableArticleExpandInFullView,
    isNewlyAddedArticle,
    isHidden,
    onCancelAddProperty,
    displayPillDeleteDialog,
    parentTemplate,
    setProperty,
    setFieldState,
    productTypeInFamilyViewContentType,
    ...rest
}) => {
    const blockedCharacters = React.useRef(
        variant !== TextVariants.newlyadded
            ? ['Enter', ...additionalBlockedCharacters]
            : additionalBlockedCharacters
    )

    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const { selectedProjectNode } = useContext(AreaContext)
    const [, forceTextUpdate] = React.useReducer((x: number) => x + 1, 0)
    const [startingCaretPosition, setStartingCaretPosition] = React.useState(-1)
    const [isMarkedDeletedText, setIsMarkedDeletedText] = React.useState(false)
    const [isUomInPill, setIsUomInPill] = React.useState(false)
    const [startIndexForPill, setStartIndexForPill] = React.useState(0)
    const [anchorIndex, setAnchorIndex] = React.useState<number | undefined>()
    const [isMounted, setIsMounted] = React.useState(false)
    const originalValue = React.useRef(value)
    const [isEverythingSelected, setIsEverythingSelected] = React.useState(
        false
    )
    const { contentItemNodes, dtv, isAddOngoing } = useReactiveVar(
        workspaceContents
    )
    const { workspaceTemplateData } = useReactiveVar(templateData)
    const { isTemplateDataLastItem } = useReactiveVar(templateFlags)

    const [inputHasSomeValue, setInputHasSomeValue] = React.useState<boolean>(
        false
    )
    const [showAddPT, setshowAddPT] = React.useState<boolean>(false)
    const [hasChanges, setHasChanges] = React.useState<boolean>(false)
    const [lastHoveredItem, setLastHoveredItem] = React.useState<Element>()
    const [currentCursorLine, setCurrentCursorLine] = React.useState<number>(1)

    const [
        prePasteSnapshot,
        setPrePasteSnapshot,
    ] = React.useState<PrePasteSnapshotInfo | null>(null)
    const { isPublishingAuthor, isAdmin } = useReactiveVar(userInfo)
    const { promoteDemoteContent } = useReactiveVar(promoteDemoteData)
    const { isPromoteDemoteTriggered } = useReactiveVar(promoteDemoteTriggered)
    const boxedElementRef = React.useRef<HTMLSpanElement>()
    const boxedUomInPillRef = React.useRef<HTMLSpanElement>()
    const [uomInPillRefs, setUomInPillRefs] = React.useState<any[]>([])

    const textRef = React.useRef<any | undefined | null>()
    const uomRef = React.useRef<any | undefined | null>()
    const pillRef = React.useRef<any | undefined | null>()
    // custom hook that provides handler (insertPill) to insert Pill, Text or UOM components
    // insertPill handler is also published as reactiveVar via 'reactiveWorkspaceActions'
    // optionally, this hook also provides handler (handleUpdateTemplate) to update the template from TabsContext
    const { insertPill } = useInsertPill()

    const isArticleText = React.useMemo(() => element?.type === 'Article', [
        element,
    ])

    const isSustainabilityTopic = React.useMemo(
        () => dtv?.name?.includes(DTVName.SUSTAINABILITY),
        [dtv]
    )

    const nonEditableFocusableArticle = React.useMemo(() => {
        return (
            (!contentTeamFromStudioPage &&
                isArticleText &&
                !template?.addingState &&
                !template?.isUserAdded) ||
            (contentTeamFromStudioPage &&
                isArticleText &&
                template?.isProductType)
        )
    }, [contentTeamFromStudioPage, isArticleText, template])

    const isElementDeleted = React.useMemo(
        () =>
            (changeType &&
                changeType.toLowerCase() ===
                    TrackChangeType.DELETED.toLowerCase()) ??
            false,
        [changeType]
    )
    const isContentEditingDisabled = React.useMemo(
        () =>
            !editable ||
            isElementDeleted ||
            (!contentTeamFromStudioPage && template?.isDeactivated),
        [
            element,
            template,
            editable,
            isElementDeleted,
            contentTeamFromStudioPage,
        ]
    )
    const isElementAdded = React.useMemo(
        () =>
            (changeType &&
                changeType.toLowerCase() ===
                    TrackChangeType.ADDED.toLowerCase()) ??
            false,
        [changeType]
    )
    const { isTrackChangeToggleOn } = useReactiveVar(showHideToggles)

    const classes = useStyles({
        editable,
        calledFromImportPage,
        isElementDeleted,
        isHidden: isHidden || template?.isDeactivated,
        isNewlyAddedArticle,
        isPremiumPlus: element?.isPremiumPlus,
        changeType: element?.changeType as TrackChangeType,
        isTrackChangeToggleOn,
        disableArticleExpandInFullView,
    })

    const text = React.useRef<string>(
        isElementDeleted
            ? value
            : variant === 'newlyadded'
            ? value === getPropertyDefaultValue()
                ? emptySpace
                : encodeEntities(decodeEntities(value))
            : encodeEntities(decodeEntities(value))
    )

    const isAddingFailed = useReactiveVar(workspaceHasError)
    const inputRef: React.RefObject<HTMLInputElement> = React.useRef(null)

    const resetText = useCallback(() => {
        setshowAddPT(true)
        setHasChanges(false)
        text.current = getPropertyDefaultValue()
        onChange?.('')
        onBlurInput?.(text.current)
        ;((inputRef?.current as unknown) as HTMLInputElement).value = ''
    }, [text, setshowAddPT, onChange, onBlurInput, setHasChanges, inputRef])

    const resetSustainabilityText = useCallback(() => {
        setshowAddPT(true)
        setHasChanges(false)
        text.current = getPropertyDefaultValue()
        onChange?.('')
        onBlurInput?.(text.current)
        if (inputRef?.current) {
            inputRef.current.value = ''
        }
    }, [text, setshowAddPT, onChange, onBlurInput, setHasChanges, inputRef])

    const getElementLinesCount = React.useCallback((element: HTMLElement) => {
        return (element && element.getClientRects().length) || 1
    }, [])
    const focusOnElementEdge = React.useCallback(
        (element: HTMLElement, isStart: boolean = false) => {
            if (element.childNodes.length > 0) {
                var range = document.createRange()
                var selection = window.getSelection()
                range.setEnd(
                    element.childNodes[0],
                    isStart ? 1 : element.innerText.length
                )
                range.setStart(
                    element.childNodes[0],
                    isStart ? 1 : element.innerText.length
                )
                range.collapse(true)
                selection?.removeAllRanges()
                selection?.addRange(range)
            }
            element.focus()
            !isStart && element.scrollIntoView()
        },
        []
    )
    const isEditable = editable && !isElementDeleted

    // calculate starting index to insert pill component
    const getInsertPillIndex = (
        titleText?: string,
        startIndexForPill: number = 0,
        key: string = ''
    ) => {
        const selection = window.getSelection()
        let startIndex = selection?.getRangeAt(0).startOffset ?? 0
        const prefixLength = titleText ? titleText.length : 0
        // in the below block, number to add is calculated based on the arrow button click
        // as the start index will be one less when arrow keys are pressed
        if (key === 'ArrowRight') {
            startIndex = startIndex + 1
        } else if (key === 'ArrowLeft') {
            startIndex = startIndex - 1
        }
        return startIndexForPill
            ? prefixLength + startIndexForPill
            : prefixLength + startIndex
    }

    const {
        isAddSpecifierNote,
        insertPillRequirements,
    } = reactiveWorkspaceActions()

    const isSMMSpecificAction =
        (isReactiveContentTeamFromStudioPage()?.value ||
            contentTeamFromStudioPage) &&
        workspaceContents()?.isDraftMode

    const handlePillRequirements = useRefCallback(
        (event: Pick<React.MouseEvent, 'currentTarget' | 'target'>) => {
            if (!isSMMSpecificAction) {
                return
            }

            const {
                uomInPill,
                plain,
                boxed,
                newlyadded,
                newPill,
                newReplacement,
            } = TextVariants

            const isSMMMOPT =
                !!contentTeamFromStudioPage && !!element?.isPremiumPlus

            const isSMMMOPTEditable = isAdmin && isSMMMOPT

            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
                isInsertUOM: false,
                isAddSpecifierNote: !!isSMMMOPTEditable,
                insertPillRequirements: {},
                insertPill: undefined,
                insertUomPill: undefined,
            })

            if (
                !isEditable ||
                (!isSMMSpecificAction && !areEq(variant, newPill))
            ) {
                return
            }

            const canAddNoteSpecifier =
                !isHidden && (isOMUser || contentTeamFromStudioPage)
            const canInsertTextOrPill =
                !areEq(element?.type, 'Article') &&
                ((areEq(variant, plain) &&
                    !areEq(insertPillRequirements?.variant, boxed)) ||
                    areEq(variant, newlyadded)) &&
                !areEq(variant, newPill)
            const canInsertUOM =
                (!areEq(element?.type, 'Article') &&
                    !areEq(variant, boxed) &&
                    !areEq(variant, newReplacement)) ||
                (areEq(insertPillRequirements?.variant, uomInPill) &&
                    !areEq(insertPillRequirements?.variant, boxed))
            const pillRequirements = {
                insertPillIndex: getInsertPillIndex(
                    titleText,
                    startIndexForPill,
                    ((event as unknown) as KeyboardEvent).code
                ),
                textWithPill: titleText ?? '',
                fieldState,
                template,
                variant: areEq(insertPillRequirements?.variant, uomInPill)
                    ? uomInPill
                    : variant,
                setFieldState,
                changeType,
                element,
                saveChanges,
                setProperty,
                titleText,
                value,
                event,
            }

            const workspaceActionTimeout = setTimeout(() => {
                setReactiveWorkspaceActions({
                    isInsertPill: canInsertTextOrPill,
                    isInsertText: canInsertTextOrPill,
                    isInsertUOM: canInsertUOM,
                    isAddSpecifierNote:
                        canAddNoteSpecifier &&
                        !areEq(variant, newlyadded) &&
                        !areEq(variant, newPill),
                    insertPillRequirements: pillRequirements,
                })
                clearTimeout(workspaceActionTimeout)
            }, 300)

            if (selectedProjectNode?.elementType === 'PRODUCTTYPE') {
                setReactiveWorkspaceActions({
                    isInsertPill: false,
                    isInsertText: false,
                    isInsertUOM: false,
                    isAddSpecifierNote: false,
                    insertPillRequirements: {},
                    insertPill: undefined,
                    insertUomPill: undefined,
                })
            }
        },
        [
            isSMMSpecificAction,
            isHidden,
            isOMUser,
            isAdmin,
            element,
            insertPillRequirements,
            titleText,
            startIndexForPill,
            setReactiveWorkspaceActions,
            fieldState,
            template,
            variant,
            setFieldState,
            changeType,
            saveChanges,
            setProperty,
            value,
            isEditable,
        ]
    )

    const isSMMSustainabilityGroup = React.useMemo(
        () =>
            isSMMSpecificAction &&
            workspaceTemplateData?.isSustainabilityContentItem,
        [isSMMSpecificAction, workspaceTemplateData]
    )

    const handleKeyDown = useRefCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!isEditable && !isSMMSustainabilityGroup) {
                return
            }

            if (nonEditableFocusableArticle) {
                haltEvent(event)
            }

            const selection = window.getSelection()
            const startIndex = isSMMSustainabilityGroup
                ? 0
                : selection?.getRangeAt(0).startOffset ?? 0
            const {
                newPill,
                newUOM,
                newlyadded,
                newReplacement,
                plain,
                uomInPill,
            } = TextVariants

            setIsEverythingSelected(false)

            // handle pill requirements when user navigates using arrow keys
            if (
                (isSMMSpecificAction &&
                    tryGetShortcutKey('ArrowLeft', event)) ||
                tryGetShortcutKey('ArrowRight', event)
            ) {
                handlePillRequirements(event)
            }

            // Determines whether the event should be propagated based on the key pressed.
            shouldHaltKeyboardEvent(event, blockedCharacters.current)

            if (
                tryGetShortcutKey('AddSpecifierNote', event) &&
                workspaceContents()?.isDraftMode &&
                (isPublishingAuthor || isOMUser)
            ) {
                const {
                    addNoteSpecifier,
                    addNoteSpecifierRequirements,
                } = reactiveWorkspaceActions()
                // invoke Note Specifier handler
                addNoteSpecifier?.(
                    addNoteSpecifierRequirements?.selectedTemplate
                )
                // then, disable "Add Specifier Note" action button
                setReactiveWorkspaceActions({
                    isAddSpecifierNote: false,
                })
            } else if (
                !isMarkedDeletedText &&
                tryGetShortcutKey('InsertUOM', event) &&
                (areEq(variant, newPill) ||
                    areEq(variant, uomInPill) ||
                    areEq(variant, newUOM))
            ) {
                haltEvent(event)
                if (areEq(variant, newPill)) {
                    setIsUomInPill(false)
                }
                setReactiveWorkspaceActions({
                    // disable insertUOM button when its not uomInPill
                    isInsertPill: false,
                    isInsertText: false,
                    isInsertUOM: areEq(variant, newPill) ? false : true,
                    insertPillRequirements: {
                        ...insertPillRequirements,
                        // to signify that the pill is created and can insert UOM in it
                        variant: uomInPill,
                        fieldState,
                        template,
                        titleText,
                        changeType,
                    },
                })

                variant = uomInPill
                insertUomPill?.(
                    startIndex,
                    event.currentTarget?.textContent ?? undefined
                )
            } else if (
                !isMarkedDeletedText &&
                isSMMSpecificAction &&
                (isPublishingAuthor || isOMUser) &&
                (tryGetShortcutKey('InsertPill', event) ||
                    tryGetShortcutKey('InsertUOM', event) ||
                    tryGetShortcutKey('InsertText', event))
            ) {
                haltEvent(event)
                if (areEq(variant, uomInPill)) {
                    return
                }

                const pillType = tryGetShortcutKey('InsertPill', event)
                    ? newPill
                    : tryGetShortcutKey('InsertText', event)
                    ? newReplacement
                    : newUOM

                const insertPillIndex = getInsertPillIndex(
                    titleText,
                    startIndexForPill
                )

                setReactiveWorkspaceActions({
                    isInsertPill: false,
                    isInsertText: false,
                    isInsertUOM: areEq(pillType, newPill),
                    insertPill,
                    insertUomPill,
                    // update insert pill requirements
                    insertPillRequirements: {
                        ...insertPillRequirements,
                        // use to signify that the pill is created and can insert UOM in it, otherwise whatever the variant is
                        variant: areEq(pillType, newPill) ? uomInPill : variant,
                        changeType,
                        fieldState,
                        template,
                        insertPillIndex,
                        pillType,
                        titleText,
                    },
                })

                // handles rendering new pill, text or UOM
                insertPillTextUOM({
                    pillType,
                    insertPillIndex,
                    changeType,
                    element,
                    event,
                    fieldState,
                    setFieldState,
                    value,
                    variant,
                    saveChanges,
                    setProperty,
                    template,
                    titleText,
                    isNotFromContentTeam: !(
                        isContentTeamUser && contentTeamFromStudioPage
                    ),
                })
            } else if (tryGetShortcutKey('Demote', event)) {
                event.preventDefault()
                let templateData = JSON.parse(JSON.stringify(template))
                templateData.contentItem.elements[0].text = text.current
                const demoteData: PromoteDemoteRequirements = {
                    content: templateData,
                    action: ActionButtonVariant.Demote,
                }
                setPromoteDemoteRequirements?.(demoteData)
            } else if (tryGetShortcutKey('Promote', event)) {
                event.preventDefault()
                let templateData = JSON.parse(JSON.stringify(template))
                templateData.contentItem.elements[0].text = text.current
                const promoteData: PromoteDemoteRequirements = {
                    content: templateData,
                    action: ActionButtonVariant.Promote,
                }
                setPromoteDemoteRequirements?.(promoteData)
            } else if (tryGetShortcutKey(ShortcutKeys.Enter, event)) {
                haltEvent(event)

                if (isArticleText) {
                    event.currentTarget?.blur()
                    return
                }
                if (variant !== plain) {
                    if ((event?.currentTarget as any)?.value === '') {
                        return
                    } else {
                        saveChanges?.([], (event?.currentTarget as any)?.value)
                        // only trigger add new line on newlyadded variants (not on Pill/Text/UOM)
                        if (variant === newlyadded) {
                            setIsAddOnQueue(true)
                        }
                        const { addQueueSib } = workspaceContents()
                        if (addQueueSib && addQueueSib?.length > 0) {
                            setAddQueueSib('')
                        }
                    }
                } else {
                    if (
                        hasChanges ||
                        (!!(event?.currentTarget as any)?.value &&
                            value !== (event?.currentTarget as any)?.value) ||
                        ((event?.currentTarget as any)?.textContent &&
                            value !==
                                (event?.currentTarget as any)?.textContent)
                    ) {
                        event.currentTarget?.blur()
                        setIsAddOnQueue(true)
                        setAddQueueSib(template?.contentItem?.id)
                    } else {
                        triggerAddNewProperty?.()
                    }
                }
            } else if (tryGetShortcutKey('Undo', event) && !!prePasteSnapshot) {
                // Undo paste
                event.preventDefault()
                if (areEq(variant, newlyadded)) {
                    var input = (inputRef?.current as unknown) as HTMLInputElement
                    if (input) {
                        const cursorPositionAfterPaste = input.selectionStart
                        const lengthOfPastedText =
                            input.value.length - prePasteSnapshot.value.length
                        input.value = prePasteSnapshot.value
                        const newCursorPosition =
                            cursorPositionAfterPaste === null
                                ? -1
                                : cursorPositionAfterPaste - lengthOfPastedText
                        if (newCursorPosition > -1) {
                            input.setSelectionRange(
                                newCursorPosition,
                                newCursorPosition
                            )
                        }
                    }
                } else {
                    text.current = prePasteSnapshot.value
                    onChange?.(prePasteSnapshot.value)

                    setStartingCaretPosition(prePasteSnapshot.caretPosition)
                }
                setPrePasteSnapshot(null)
            } else if (
                tryGetShortcutKey('Escape', event) &&
                (areEq(variant, newPill) ||
                    areEq(variant, newUOM) ||
                    areEq(variant, newReplacement)) &&
                areEq('', text.current.trim())
            ) {
                haltEvent(event)
                displayPillDeleteDialog?.(true)
            } else if (
                tryGetShortcutKey('Escape', event) &&
                areEq(variant, newlyadded) &&
                isArticleText
            ) {
                haltEvent(event)
                onCancelAddProperty?.()
            } else if (tryGetShortcutKey('SelectAll', event)) {
                setIsEverythingSelected(true)
                document.execCommand('selectAll', false)
            } else if (
                (tryGetShortcutKey(ShortcutKeys.Backspace, event) ||
                    areEq(event.key, '48') ||
                    tryGetShortcutKey(ShortcutKeys.Delete, event) ||
                    tryGetShortcutKey('Close', event)) &&
                variant === plain &&
                (!isElementAdded || isDeleteFormatted)
            ) {
                haltEvent(event)

                if (
                    selection &&
                    !areEq(
                        textToBeDeleted,
                        (selection?.anchorNode as any)?.id
                    ) &&
                    !areEq(
                        emptySpace,
                        (selection?.anchorNode as any)?.textContent
                    )
                ) {
                    const eventElementNodes = [
                        ...(event.currentTarget.childNodes as any),
                    ]
                    const isDeleteKey = !!tryGetShortcutKey(
                        ShortcutKeys.Delete,
                        event
                    )
                    const deleteAnchorIndex = getAnchorIndex(
                        eventElementNodes,
                        selection as Selection
                    )

                    let formattedTextDetails: FormattedText | null = null
                    const isAtTheBeginning =
                        startIndex > 0
                            ? (areEq(selection?.type, 'Range') || isDeleteKey
                                  ? startIndex
                                  : startIndex - 1) === 0
                            : true

                    formattedTextDetails = formatNewText(
                        TextFormatType.delete,
                        {
                            eventElementNodes,
                            anchorIndex: deleteAnchorIndex,
                            selection: selection as Selection,
                            variant,
                            startIndex,
                        },
                        {
                            isDeleteKey,
                            isNewlyAdded:
                                areEq(
                                    (eventElementNodes?.[
                                        deleteAnchorIndex
                                    ] as any)?.id,
                                    textToBeAdded
                                ) ||
                                (isElementAdded && !isDeleteFormatted),
                            currentText: event?.currentTarget?.innerText,
                            isToFormat: isDeleteFormatted ?? true,
                            isAtTheBeginning:
                                isAtTheBeginning && deleteAnchorIndex === 0,
                        },
                        undefined,
                        contentTeamFromStudioPage
                    )

                    let cursorNode = isDeleteKey
                        ? deleteAnchorIndex + 2
                        : deleteAnchorIndex
                    let cursorPosition =
                        isDeleteKey || isAtTheBeginning
                            ? 0
                            : (formattedTextDetails?.caretPosition as number)
                    if (
                        isAtTheBeginning &&
                        !areEq(
                            textToBeDeleted,
                            eventElementNodes[deleteAnchorIndex]?.id
                        ) &&
                        !isElementAdded
                    ) {
                        if (deleteAnchorIndex > 0) {
                            if (
                                areEq(
                                    eventElementNodes[deleteAnchorIndex - 1]
                                        ?.id,
                                    textToBeAdded
                                ) ||
                                areEq(
                                    eventElementNodes[deleteAnchorIndex]?.id,
                                    textToBeAdded
                                )
                            ) {
                                cursorNode = deleteAnchorIndex - 1
                                if (
                                    areEq(
                                        eventElementNodes[cursorNode]?.id,
                                        textToBeDeleted
                                    )
                                ) {
                                    cursorPosition = 0
                                } else {
                                    cursorPosition = (
                                        eventElementNodes[cursorNode]
                                            ?.outerText ??
                                        eventElementNodes[cursorNode]
                                            ?.textContent
                                    )?.length
                                }
                            }
                        }
                    }

                    setAnchorIndex(cursorNode)

                    setStartingCaretPosition(cursorPosition)
                    text.current = formattedTextDetails?.text!
                    setHasChanges(true)

                    if (cursorPosition < 1) {
                        setIsMarkedDeletedText(true)
                    }
                } else {
                    setIsMarkedDeletedText(true)
                }
            } else if (tryGetShortcutKey('Copy', event)) {
                // To handle copy function
            } else if (
                //handles move up shortcut key for workspace action bar
                tryGetShortcutKey('MoveUp', event)
            ) {
                //Move Up shortcut key disabled when paragraph is first order
                if (
                    workspaceTemplateData &&
                    workspaceTemplateData.order !== 0
                ) {
                    setMoveUpAndDownTrigger({
                        isMoveUpAndDownTriggered: true,
                        action: ActionButtonVariant.MoveUp,
                        isShortcutKey: true,
                    })
                }
            } else if (
                //handles move down shortcut key for workspace action bar
                tryGetShortcutKey('MoveDown', event)
            ) {
                if (workspaceTemplateData) {
                    const isArticleSelected =
                        workspaceTemplateData?.articleId ===
                        workspaceTemplateData?.contentItem?.id
                    const isLastArticle =
                        workspaceTemplateData?.paragraphOrder ===
                        getArticles(
                            contentItemNodes,
                            dtv!,
                            workspaceTemplateData?.partType
                        )?.length
                    //Move Down shortcut key disabled when paragraph is last order
                    if (
                        isArticleSelected
                            ? !isLastArticle
                            : !isTemplateDataLastItem
                    ) {
                        setMoveUpAndDownTrigger({
                            isMoveUpAndDownTriggered: true,
                            action: ActionButtonVariant.MoveDown,
                            isShortcutKey: true,
                        })
                    }
                }
            } else if (tryGetShortcutKey('Save', event)) {
                haltEvent(event)
                event.currentTarget?.blur()
            } else {
                const validKeysRegex = new RegExp('^[a-zA-Z1-9-.:, ]{1}$')
                if (
                    selection &&
                    areEq(selection?.type, 'Range') &&
                    validKeysRegex.test(event.key) &&
                    variant === plain &&
                    (!isElementAdded || isDeleteFormatted)
                ) {
                    haltEvent(event)

                    const focusText = selection?.getRangeAt(0).cloneContents()
                        .textContent
                    const eventNodes = [
                        ...(event.currentTarget.childNodes as any),
                    ]
                    const replacedAnchorIndex = getAnchorIndex(
                        eventNodes,
                        selection
                    )
                    if (!!focusText) {
                        const formattedReplacedText: FormattedText = formatNewText(
                            TextFormatType.replace,
                            {
                                eventElementNodes: eventNodes,
                                anchorIndex: replacedAnchorIndex,
                                variant,
                                startIndex,
                                selection: selection as Selection,
                            },
                            undefined,
                            {
                                replacedBy: getAddedDiv(event?.key),
                                replacedWord:
                                    isDeleteFormatted &&
                                    !areEq(
                                        (eventNodes?.[
                                            replacedAnchorIndex
                                        ] as any)?.id,
                                        textToBeAdded
                                    )
                                        ? getDeletedDiv(focusText)
                                        : '',
                            },
                            contentTeamFromStudioPage
                        )
                        text.current = formattedReplacedText.text

                        const isAtTheBeginning =
                            startIndex > 0
                                ? (areEq(selection?.type, 'Range')
                                      ? startIndex
                                      : startIndex - 1) === 0
                                : true

                        setAnchorIndex(
                            isDeleteFormatted &&
                                !isAtTheBeginning &&
                                !areEq(
                                    (eventNodes?.[replacedAnchorIndex] as any)
                                        ?.id,
                                    textToBeAdded
                                )
                                ? replacedAnchorIndex + 2
                                : replacedAnchorIndex + 1
                        )
                        setStartingCaretPosition(1)
                        setHasChanges(true)
                    }
                }
                // it enables user to navigate through paragraphs using up/down arrow keys
                if (
                    tryGetShortcutKey(ShortcutKeys.ArrowDown, event) ||
                    tryGetShortcutKey(ShortcutKeys.ArrowUp, event)
                ) {
                    const allEditableElements = (Array.from(
                        document.querySelectorAll(
                            '[id*=spanElementText],[id*=inputElementText],[id*=replacementOptionText]'
                        )
                    ) as HTMLElement[]).filter((item) => item.isContentEditable)

                    const focusNodeElementId =
                        (selection?.anchorNode?.parentNode as HTMLElement).id ||
                        (selection?.anchorNode?.parentNode as HTMLElement)
                            .dataset.testid

                    const currentIndex = allEditableElements.findIndex(
                        (item) => focusNodeElementId === item?.id
                    )
                    const elementLinesCount = getElementLinesCount(
                        allEditableElements[currentIndex]
                    )
                    const currentElement = allEditableElements[currentIndex]
                    const { lastElementCursorIndexIds } = workspaceContents()
                    const currentCursorLine =
                        focusNodeElementId &&
                        lastElementCursorIndexIds &&
                        lastElementCursorIndexIds[focusNodeElementId]
                            ? lastElementCursorIndexIds[focusNodeElementId]
                            : 1
                    if (tryGetShortcutKey(ShortcutKeys.ArrowDown, event)) {
                        if (currentCursorLine >= elementLinesCount) {
                            if (currentIndex < allEditableElements.length - 1) {
                                const nextElement =
                                    allEditableElements[currentIndex + 1]
                                updateLastElementIndexId({
                                    ...lastElementCursorIndexIds,
                                    [nextElement.id]: 1,
                                    [currentElement.id]: elementLinesCount,
                                })
                                if (
                                    (nextElement.innerText &&
                                        !areEq(
                                            nextElement.innerText[0],
                                            ' '
                                        )) ||
                                    areEq(nextElement.innerText, ' ')
                                ) {
                                    nextElement.focus()
                                } else {
                                    focusOnElementEdge(nextElement, true)
                                }
                                haltEvent(event)
                            }
                        } else {
                            updateLastElementIndexId({
                                ...lastElementCursorIndexIds,
                                [currentElement.id]: currentCursorLine + 1,
                            })
                        }
                    } else {
                        if (currentCursorLine <= 1) {
                            if (currentIndex > 0) {
                                const nextElement =
                                    allEditableElements[currentIndex - 1]
                                updateLastElementIndexId({
                                    ...lastElementCursorIndexIds,
                                    [nextElement.id]: getElementLinesCount(
                                        nextElement
                                    ),
                                    [currentElement.id]: 1,
                                })
                                focusOnElementEdge(nextElement)
                                haltEvent(event)
                            }
                        } else {
                            updateLastElementIndexId({
                                ...lastElementCursorIndexIds,
                                [currentElement.id]: currentCursorLine - 1,
                            })
                        }
                    }
                    handlePillRequirements(event)
                }
            }
            if (tryGetShortcutKey(ShortcutKeys.Enter, event)) {
                // This block will remove focus from  Pills, ReplacementOption, UOM on pressing enter
                // TODO: narrow refObject types if possible
                if (areEq(variant, newReplacement)) {
                    ;(textRef?.current as HTMLInputElement)?.blur()
                } else if (areEq(variant, newUOM)) {
                    ;(uomRef?.current as HTMLInputElement)?.blur()
                } else {
                    ;((pillRef?.current as unknown) as HTMLInputElement)?.blur()
                }
            }
        },
        [
            isMarkedDeletedText,
            isEditable,
            nonEditableFocusableArticle,
            isDeleteFormatted,
            blockedCharacters,
            prePasteSnapshot,
            text,
            template,
            fieldState,
            onChange,
            value,
            variant,
            isEverythingSelected,
            originalValue,
            anchorIndex,
            titleText,
            triggerAddNewProperty,
            startIndexForPill,
            inputHasSomeValue,
            setInputHasSomeValue,
            currentCursorLine,
            getElementLinesCount,
            setCurrentCursorLine,
            focusOnElementEdge,
            workspaceContents,
            updateLastElementIndexId,
            workspaceTemplateData,
            isTemplateDataLastItem,
            setReactiveWorkspaceActions,
            handlePillRequirements,
            isSMMSpecificAction,
            insertPillRequirements,
            contentTeamFromStudioPage,
        ]
    )

    // this block of code updates the action bar buttons
    React.useEffect(() => {
        if (isMarkedDeletedText) {
            setReactiveWorkspaceActions(undefined)
        }
    }, [isMarkedDeletedText])

    const handleInputKeyUp = useCallback(
        (
            event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
            if (isPublishingAuthor && variant === TextVariants.newlyadded) {
                const length = (event?.currentTarget as HTMLInputElement)?.value
                    .length
                if (length > 0 && !inputHasSomeValue) {
                    setInputHasSomeValue(true)
                }
                if (length === 0 && inputHasSomeValue) {
                    setInputHasSomeValue(false)
                }
            }
            setshowAddPT(false)
        },
        [inputHasSomeValue, setInputHasSomeValue, setshowAddPT]
    )

    const handleAddKeyDown = useCallback(
        (
            event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
            handleKeyDown(event as React.KeyboardEvent<HTMLInputElement>)
        },
        [handleKeyDown]
    )

    // (for InsertUOM Button) to prevent invoking immediate save changes when inserting UOM inside pill
    const isBlurInsertUOMButton = useCallback(
        (
            event:
                | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                | undefined
        ) => {
            // Only prevent blur save when actually clicking UOM-related buttons
            const relatedTarget = (event as React.FocusEvent<HTMLDivElement>)
                .relatedTarget as HTMLElement
            const isClickingUOMButton =
                relatedTarget?.dataset?.testid === 'InsertUOM'

            if (
                relatedTarget &&
                areEq(
                    insertPillRequirements?.variant,
                    TextVariants.uomInPill
                ) &&
                isClickingUOMButton
            ) {
                setReactiveWorkspaceActions({
                    isInsertPill: false,
                    isInsertText: false,
                    isInsertUOM: true,
                    isAddSpecifierNote: false,
                    insertUomPill, // get the latest published handler to fix 'race condition' bug
                    insertPillRequirements: {
                        ...insertPillRequirements,
                        fieldState,
                        setFieldState,
                        changeType,
                        insertPillIndex:
                            window.getSelection()?.getRangeAt(0).startOffset ??
                            0,
                        textWithPill:
                            event?.target?.innerText ??
                            insertPillRequirements.textWithPill,
                    },
                })
                return true
            }

            // disable OM action buttons
            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
                isInsertUOM: false,
                isAddSpecifierNote: false,
                insertUomPill,
                insertPillRequirements,
            })

            return false
        },
        [setFieldState, fieldState, insertPillRequirements, insertUomPill]
    )

    const handleBlur = useRefCallback(
        (
            event:
                | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                | undefined
        ) => {
            if (!editable) {
                return
            }

            if (showAddPT) {
                ;((inputRef?.current as unknown) as HTMLInputElement).value = ''
                return
            }
            if (
                inputRef &&
                inputRef?.current &&
                document.activeElement !== inputRef?.current
            ) {
                ;((inputRef?.current as unknown) as HTMLInputElement)?.focus()
            }
            const { isCancelAddDialogOpen } = workspaceContents()
            if (
                event &&
                event.target &&
                event.target?.value &&
                event.target?.value !== '' &&
                hasChanges &&
                variant === TextVariants.newlyadded &&
                !isCancelAddDialogOpen
            ) {
                if (!isPromoteDemoteTriggered) {
                    saveChanges?.([], event.target?.value)
                    setHasChanges(false)
                    newlyAddedParagraphText('')
                    setPromoteDemoteWorkspaceTemplateData(undefined)
                }
                setPromoteDemoteTriggered({
                    isPromoteDemoteTriggered: false,
                })
            } else {
                if (isBlurInsertUOMButton(event)) {
                    return
                }

                const parent = document.getElementById(TextVariants.uomInPill)
                // use currentTarget object for variants not related to Promote and Demote to prevent invoking immediate save changes
                const isInParent = event?.currentTarget?.contains(
                    event.relatedTarget as any
                )
                if (
                    document.getElementById(TextVariants.uomInPill) &&
                    !isInParent
                ) {
                    onBlurInput?.(text.current, TextVariants.uomInPill, event)
                    return
                } else if (event && parent === null) {
                    onBlurInput?.(text.current)
                }
            }
        },
        [
            editable,
            inputRef,
            saveChanges,
            hasChanges,
            onBlurInput,
            variant,
            showAddPT,
            isPromoteDemoteTriggered,
        ]
    )

    const handleHtmlParagraphPaste = useCallback(
        (clipboardHtml: string) => {
            // Discard any edits to the active item user pasted into
            onHtmlParagraphPaste?.(clipboardHtml)
        },
        [onHtmlParagraphPaste]
    )

    // this block of code calculates the start and end index of the text that is being pasted
    const getCalculatedIndex = useCallback(
        (selection) => {
            const parentNode = selection?.anchorNode?.parentNode
            const isNewInsert = parentNode?.id === textToBeAdded
            // if the user types some text and then paste the copied text, this block is executed
            if (isNewInsert) {
                const nextElement = parentNode?.nextSibling?.data.replace(
                    /\s/g,
                    ' '
                )
                const fullText = decodeEntities(
                    parentNode?.offsetParent?.innerHTML
                ).replace(/\s/g, ' ')

                return fullText?.indexOf(`</div>${nextElement}`)
            }
            return selection?.getRangeAt(0).startOffset ?? 0
        },
        [text]
    )

    const handlePaste = useRefCallback(
        (event: React.ClipboardEvent<HTMLDivElement>) => {
            if (!editable) {
                return
            }

            // If user is pasting HTML paragraphs (e.g., from Word), call our handler function for that
            if (
                tryHandleHtmlParagraphPaste(
                    handleHtmlParagraphPaste,
                    event,
                    template
                )
            ) {
                // Handler function took care of it
                return
            }

            //get initial cursor position
            const clipboardText = event.clipboardData.getData('text')
            const selection = window.getSelection()
            const startIndex = getCalculatedIndex(selection)
            const endIndex = getCalculatedIndex(selection)
            const selectionLength = endIndex - startIndex

            const decodedCurrentValue = decodeEntities(text.current)

            if (
                containsBlockedCharacters(
                    clipboardText,
                    blockedCharacters.current
                ) ||
                decodedCurrentValue.length - selectionLength >= maxChars
            ) {
                haltEvent(event)
            } else {
                setPrePasteSnapshot({
                    value: text.current,
                    caretPosition: startIndex,
                })

                if (variant === TextVariants.plain) {
                    haltEvent(event)
                    const combinedLength =
                        decodedCurrentValue.length +
                        clipboardText.length -
                        selectionLength
                    const currentDecodedClipboardText = decodeEntities(
                        clipboardText
                    )
                    const lengthToPaste =
                        combinedLength > maxChars
                            ? maxChars -
                              (decodedCurrentValue.length - selectionLength)
                            : currentDecodedClipboardText.length
                    const textBeforeCurrentCursorPosition = decodedCurrentValue.substring(
                        0,
                        startIndex
                    )
                    const textAfterCurrentCursorPosition = decodedCurrentValue.substring(
                        endIndex
                    )
                    const clipboardItemsToInsert = currentDecodedClipboardText.substring(
                        0,
                        lengthToPaste
                    )
                    // checking is the user is adding new text and then pasting copied value
                    const isAlreadyNewTextInsert =
                        (selection?.anchorNode?.parentNode as HTMLElement)
                            ?.id === textToBeAdded

                    const textToPaste = `${textBeforeCurrentCursorPosition}${
                        isAlreadyNewTextInsert
                            ? clipboardItemsToInsert // if new text is being inserted, then add the pasted text into the new text container
                            : getAddedDiv(clipboardItemsToInsert)
                    }${textAfterCurrentCursorPosition}`

                    text.current = textToPaste

                    const pasteAnchorIndex = [
                        ...(event.currentTarget.childNodes as any),
                    ].findIndex((childNode: any) => {
                        return (
                            childNode.isEqualNode(selection?.anchorNode) ||
                            (childNode as any).childNodes?.[0]?.isEqualNode(
                                selection?.anchorNode
                            )
                        )
                    })
                    setStartingCaretPosition(
                        isAlreadyNewTextInsert ? 0 : lengthToPaste
                    )
                    setAnchorIndex(pasteAnchorIndex + 1)
                    setHasChanges(true)
                } else if (variant === TextVariants.newlyadded) {
                    var input = (inputRef?.current as unknown) as HTMLInputElement
                    if (input) {
                        // Don't use default behavior; we want to limit max characters
                        haltEvent(event)
                        const startIndex = input.selectionStart ?? 0
                        const endIndex = input.selectionEnd ?? 0
                        const selectionLength = endIndex - startIndex
                        const currentValue = input.value
                        const combinedLength =
                            currentValue.length +
                            clipboardText.length -
                            selectionLength
                        const currentDecodedClipboardText = decodeEntities(
                            clipboardText
                        )
                        const lengthToPaste =
                            combinedLength > maxChars
                                ? maxChars -
                                  (currentValue.length - selectionLength)
                                : currentDecodedClipboardText.length
                        const textBeforeCurrentCursorPosition = currentValue.substring(
                            0,
                            startIndex
                        )
                        const textAfterCurrentCursorPosition = currentValue.substring(
                            endIndex
                        )
                        const textToInsert = currentDecodedClipboardText.substring(
                            0,
                            lengthToPaste
                        )

                        const textToPaste = `${textBeforeCurrentCursorPosition}${textToInsert}${textAfterCurrentCursorPosition}`
                        input.value = textToPaste
                        const positionAfterPaste =
                            textBeforeCurrentCursorPosition.length +
                            textToInsert.length
                        input.setSelectionRange(
                            positionAfterPaste,
                            positionAfterPaste
                        )
                        setHasChanges(true)
                    }
                }
            }
        },
        [text, onChange, blockedCharacters, template, inputRef]
    )
    const { isMoveUpAndDownTriggered } = useReactiveVar(moveUpAndDown)

    const checkMoveRunningToDisableActionBar = useRefCallback(() => {
        if (!isMoveUpAndDownTriggered) {
            setWorkspaceTemplateData(undefined)
        }
        if (variant !== TextVariants.newlyadded) {
            setPromoteDemoteWorkspaceTemplateData(undefined)
        }
    }, [isMoveUpAndDownTriggered, !isPromoteDemoteTriggered])
    const handleArticleFocus = useCallback(
        (event: React.FocusEvent<HTMLDivElement> | undefined) => {
            setWorkspaceTemplateData({ workspaceTemplateData: template })
        },
        [template, element]
    )
    const handleBlurFamilyTitle = useRefCallback(
        (
            event:
                | React.FocusEvent<HTMLDivElement>
                | React.KeyboardEvent<HTMLDivElement>
        ) => {
            if (!editable) {
                return
            }

            if (!(event as React.FocusEvent<HTMLDivElement>).relatedTarget) {
                const checkMoveTimeout = setTimeout(() => {
                    checkMoveRunningToDisableActionBar()

                    clearTimeout(checkMoveTimeout)
                }, 1000)
            }
            if (hasChanges || value !== event.currentTarget.textContent) {
                const eventElementNodes = Array.from(
                    event.currentTarget.childNodes as NodeList
                )?.map((eventNode) => {
                    if (eventNode.nodeType === 1) {
                        return {
                            id: (eventNode as any).id,
                            textContent: sanitizeZeroWidthText(
                                eventNode?.textContent!
                            ),
                            outerText: sanitizeZeroWidthText(
                                (eventNode as any).outerText
                            ),
                        }
                    }
                    return eventNode
                })
                let isTextInBetweenPill = false
                let pill = event.currentTarget.childNodes as NodeList
                for (let i = 0; i <= pill?.length; i++) {
                    if (pill[i]?.textContent === ' ' || pill?.length === 1) {
                        isTextInBetweenPill = true
                    }
                }
                let newTextContentElement = extractUpdatedElementNodes(
                    eventElementNodes,
                    element!,
                    changeType,
                    titleText,
                    !(isContentTeamUser && contentTeamFromStudioPage),
                    template?.isUserAdded
                )?.filter((element: TextContentElement) => {
                    return (
                        element?.text !== '' && !element.text?.match(/\u200d/g)
                    )
                })

                if (
                    newTextContentElement?.[0]?.text?.includes(':') &&
                    newTextContentElement?.[0]?.text?.split(':')[1].trim()
                ) {
                    if (!element?.text?.includes(':')) {
                        newTextContentElement.splice(
                            0,
                            1,
                            {
                                ...newTextContentElement[0],
                                text:
                                    newTextContentElement[0].text.split(
                                        ':'
                                    )[0] + ': ',
                                changeType: null,
                            },
                            {
                                ...newTextContentElement[0],
                                text: newTextContentElement[0].text
                                    .split(':')[1]
                                    .trim(),
                            }
                        )
                    }
                }

                /*   
     After typing into plain text , when action bar button clicked for create pill / UOM / insert option , 
     this block of code is to stop saving the changes for the continuous insertion of clicked option item
*/
                const relatedTargetType = (event as React.FocusEvent<
                    HTMLDivElement
                >)?.relatedTarget as HTMLButtonElement
                if (
                    relatedTargetType?.dataset?.testid === 'InsertPill' ||
                    relatedTargetType?.dataset?.testid === 'InsertText' ||
                    relatedTargetType?.dataset?.testid === 'InsertUOM'
                ) {
                    return
                }

                saveChanges?.(
                    newTextContentElement,
                    !!titleText
                        ? `${titleText}:${event?.currentTarget?.textContent}`
                        : `${event?.currentTarget?.textContent}`
                )
                setHasChanges(false)
            }
        },
        [
            editable,
            saveChanges,
            changeType,
            element,
            titleText,
            hasChanges,
            value,
        ]
    )

    const handleChange = useRefCallback(
        (event: ContentEditableEvent, type?: string, index?: number) => {
            if (nonEditableFocusableArticle) {
                return
            }
            if (variant === TextVariants.newlyadded) {
                if (isAddOngoing) {
                    setIsAddOngoing(false)
                }
                newlyAddedParagraphText(event?.target?.value)
            }
            if (isArticleText) {
                onChange?.(event?.target?.value)
            }
            if (variant === TextVariants.uomInPill) {
                setIsUomInPill(true)
                handleChangepillInUom!(event, type, index)
                return
            }
            if (
                variant === TextVariants.newPill &&
                element?.elements!?.length > 0
            ) {
                setIsUomInPill(true)
                onChange?.(event?.target?.value, type, index)

                return
            }
            const selection = window.getSelection()
            const startIndex = selection?.getRangeAt(0).startOffset ?? 0
            const elementNodes = [...event.currentTarget.childNodes]
            const anchorIndex = elementNodes.findIndex((childNode: any) =>
                childNode.isEqualNode(selection?.anchorNode)
            )
            const newUOMMaxChars = 50
            const boxedMaxChars = maxTextLength
            let cursorNode = anchorIndex
            const textValues =
                (variant === TextVariants.newUOM ||
                    variant === TextVariants.boxed) &&
                event.target.value.length > 5
                    ? event.target.value.slice(
                          0,
                          variant === TextVariants.boxed
                              ? boxedMaxChars
                              : newUOMMaxChars
                      ) // if variant is newUOM it will only accepts 50 characters and boxed accepts maxTextLength characters
                    : elementNodes?.length > 0 &&
                      elementNodes?.some((node) => node.nodeType !== 3) &&
                      variant !== TextVariants.newlyadded
                    ? elementNodes
                          .filter(
                              (node) =>
                                  node.nodeType === 3 ||
                                  !node.id.includes(textToBeDeleted)
                          )
                          ?.reduce((prev, current) => {
                              return `${prev}${
                                  current.nodeType === 3
                                      ? current.textContent
                                      : current.outerHTML
                              }`
                          }, '')
                    : event.target.value
            const newVal = textValues.replace(/(?:^(?:&nbsp;)+)/g, '')
            const sanitizedNewVal = sanitizeHtml(newVal, { allowedTags: [] })
                ?.replace(/&lt;/g, '<')
                ?.replace(/&gt;/g, '>')
                ?.replace(/&amp;/g, '&')
                ?.replace(/&quot;/g, '"')

            // create a decoded version of new val
            // in order to accurately count characters
            const decodedNewVal = sanitizeAndDecodeValue(newVal)
            const decodedCurrentVal = decodeEntities(text.current)
            if (
                (decodedCurrentVal.length < maxChars &&
                    decodedNewVal.length <= maxChars) ||
                (decodedCurrentVal.length >= maxChars &&
                    decodedNewVal.length < decodedCurrentVal.length)
            ) {
                // This is a workaround for firefox inserting
                // <br> tag in empty inline contenteditable elements
                if (
                    (sanitizedNewVal.length === 4 &&
                        decodedNewVal === '<br>') ||
                    (sanitizedNewVal.length === 6 && decodedNewVal === '&nbsp;')
                ) {
                    onChange?.('')
                } else {
                    let newFormattedText = ''
                    if (
                        variant !== TextVariants.plain ||
                        (isElementAdded && !isDeleteFormatted)
                    ) {
                        newFormattedText = sanitizedNewVal
                    } else {
                        if (anchorIndex >= 0) {
                            newFormattedText = formatNewText(
                                TextFormatType.add,
                                {
                                    eventElementNodes: elementNodes,
                                    startIndex,
                                    selection: selection as Selection,
                                    anchorIndex,
                                    variant,
                                },
                                undefined,
                                undefined,
                                contentTeamFromStudioPage
                            )?.text

                            setStartIndexForPill(
                                (prev) => prev + (startIndex - 1)
                            )
                            const isAtTheBeginning =
                                startIndex > 0
                                    ? (selection?.type === 'Range'
                                          ? startIndex
                                          : startIndex - 1) === 0
                                    : true

                            cursorNode =
                                isAtTheBeginning && anchorIndex === 0
                                    ? 0
                                    : anchorIndex + 1

                            if (
                                isElementAdded &&
                                isDeleteFormatted &&
                                !elementNodes?.[anchorIndex]?.id
                            ) {
                                //manually manipulating nodes to overwrite merging of conteneditables
                                let anchorNode = elementNodes?.[anchorIndex]

                                const wordInAction = anchorNode?.textContent?.charAt(
                                    startIndex - 1
                                )
                                ;(elementNodes?.[
                                    anchorIndex
                                ] as Node).nodeValue = anchorNode?.textContent?.substring(
                                    0,
                                    startIndex - 1
                                )

                                const addedNode = document.createElement(
                                    'template'
                                )
                                addedNode.setAttribute(
                                    'contenteditable',
                                    'true'
                                )
                                addedNode.textContent = wordInAction
                                addedNode.id = textToBeAdded
                                addedNode.style.color = '#FF6060'
                                addedNode.style.display = 'inline'

                                const elNode = document.getElementById(
                                    identifier ?? ''
                                )?.parentNode?.firstChild as Node
                                elNode.insertBefore(
                                    addedNode,
                                    elNode.childNodes[anchorIndex + 1]
                                )
                                setStartingCaretPosition(1)
                                setAnchorIndex(cursorNode)
                            }

                            if (
                                elementNodes[cursorNode - 1]?.id ===
                                    textToBeDeleted ||
                                (elementNodes[anchorIndex - 1]?.id ===
                                    textToBeDeleted &&
                                    isAtTheBeginning)
                            ) {
                                cursorNode = cursorNode - 1
                            }
                        } else {
                            elementNodes.forEach((node) => {
                                newFormattedText =
                                    node.nodeType === 3
                                        ? `${newFormattedText}${node.textContent}`
                                        : node.nodeType === 1 &&
                                          ((node as any)?.id ===
                                              textToBeAdded ||
                                              (node as any)?.id ===
                                                  textToBeDeleted)
                                        ? `${newFormattedText}${sanitizeZeroWidthText(
                                              node.outerHTML
                                          )}`
                                        : `${newFormattedText}`
                                if (
                                    //empty div placeholder for delete all
                                    anchorIndex === -1 &&
                                    selection?.anchorNode?.textContent ===
                                        node.textContent &&
                                    !(node as any)?.id
                                ) {
                                    newFormattedText = `${newFormattedText}${getAddedDiv(
                                        sanitizeZeroWidthText(node.textContent)
                                    )}`
                                }
                            })
                        }
                    }

                    text.current = newFormattedText
                    if (variant === TextVariants.plain) {
                        if (cursorNode !== -1) {
                            if (
                                !changeType ||
                                changeType.toLowerCase() !==
                                    TrackChangeType.ADDED.toLowerCase()
                            ) {
                                setStartingCaretPosition(1)
                                setAnchorIndex(cursorNode)
                            }
                        } else {
                            const previousSiblingIndex =
                                elementNodes?.findIndex((x) =>
                                    x.isEqualNode(
                                        selection?.anchorNode?.parentNode
                                            ?.previousSibling
                                    )
                                ) ?? 0
                            if (
                                cursorNode === -1 &&
                                elementNodes
                                    ?.slice(previousSiblingIndex)
                                    .findIndex((x) =>
                                        x.isEqualNode(
                                            selection?.anchorNode?.parentNode
                                        )
                                    ) === 0
                            ) {
                                setAnchorIndex(0)
                                setStartingCaretPosition(
                                    selection?.anchorNode?.parentNode
                                        ?.textContent?.length ?? 0
                                )
                            }
                        }
                    } else if (
                        variant === TextVariants.boxed ||
                        (variant === TextVariants.newlyadded &&
                            cursorNode !== -1) ||
                        !changeType
                    ) {
                        setAnchorIndex(cursorNode)
                    }

                    if (
                        variant === TextVariants.boxed ||
                        variant === TextVariants.newUOM ||
                        variant === TextVariants.newReplacement
                    ) {
                        onChange?.(newFormattedText)
                    }
                    setHasChanges(true)
                }
            } else {
                setStartingCaretPosition(startIndex - 1)
                onChange?.(text.current)
            }
        },
        [
            text,
            isUomInPill,
            identifier,
            onChange,
            variant,
            titleText,
            changeType,
            element,
            isDeleteFormatted,
            nonEditableFocusableArticle,
            isAddOngoing,
            contentTeamFromStudioPage,
        ]
    )
    const handleMouseEnter = useRefCallback(
        (e) => {
            if (
                !lastHoveredItem ||
                (lastHoveredItem && lastHoveredItem.id !== e.target.id)
            ) {
                setLastHoveredItem(e.target)
            }
        },
        [lastHoveredItem, setLastHoveredItem]
    )
    const handleMouseLeave = useRefCallback(
        (e) => setLastHoveredItem(undefined),
        [setLastHoveredItem]
    )
    const isDeleteIconVisible = React.useMemo(() => {
        return (
            (selectedProjectNode?.elementType === 'PRODUCTFAMILY' ||
                selectedProjectNode?.elementType === 'PRODUCTTYPE') &&
            contentTeamFromStudioPage &&
            (variant === TextVariants.boxed ||
                (variant === TextVariants.newUOM && !!element?.imperialUnits) ||
                !!element?.metricUnits) &&
            editable
        )
    }, [
        selectedProjectNode,
        contentTeamFromStudioPage,
        variant,
        element,
        editable,
    ])

    // useEffect when promote or demote content was triggered through the workspace action bar
    React.useEffect(() => {
        if (
            promoteDemoteContent?.content?.id == template?.id &&
            promoteDemoteContent !== undefined &&
            fieldState?.id === promoteDemoteContent?.content?.elementId
        ) {
            setPromoteDemoteRequirements?.(promoteDemoteContent)
            setPromoteDemoteContent(undefined)
        }
    }, [promoteDemoteContent, fieldState])

    // side-effect to set current states of ActionButtons
    React.useEffect(() => {
        if (
            !!!workspaceTemplateData &&
            selectedProjectNode?.elementType !== 'PRODUCTTYPE'
        ) {
            // timeout, so just the user can click the 'Note Specifier' action button before disabling
            const updateWSActionTimeout = setTimeout(() => {
                setReactiveWorkspaceActions(undefined)
                clearTimeout(updateWSActionTimeout)
            }, 100)
        }
    }, [workspaceTemplateData])

    //moves caret to position
    React.useEffect(() => {
        if (startingCaretPosition >= 0) {
            const range = document.createRange()
            const selection = window.getSelection()

            const el = document.getElementById(identifier ?? '')
            let node = anchorIndex
                ? el?.childNodes[anchorIndex]
                : el?.childNodes[0]
            if ((node as any)?.id === textToBeDeleted) {
                //if it is a not editable node, look for nearest editable
                node = Array.from(el?.childNodes!)
                    ?.slice(anchorIndex)
                    ?.find(
                        (x) => (x as any).id === textToBeAdded || !(x as any).id
                    )

                if (node?.textContent === '') {
                    node = Array.from(el?.childNodes!)
                        ?.slice(0, anchorIndex)
                        ?.reverse()
                        ?.find(
                            (x) =>
                                (x as any).id === textToBeAdded ||
                                !(x as any).id
                        )
                }
            }
            if (node) {
                const textLength = decodeEntities(node.textContent as string)
                    .length
                const resultingCaretPosition =
                    startingCaretPosition <= textLength
                        ? startingCaretPosition
                        : textLength

                range.setStart(
                    (node as any).id === textToBeAdded
                        ? node.firstChild
                            ? (node.firstChild as Node)
                            : node
                        : node,
                    resultingCaretPosition
                )
                range.collapse(true)
                selection?.removeAllRanges()
                selection?.addRange(range)
            }
            setStartingCaretPosition(-1)
            setAnchorIndex(undefined)
        }
    }, [startingCaretPosition, identifier, text, anchorIndex, isUomInPill])

    // Check if temporary paragraphs have initial value
    React.useEffect(() => {
        setIsMounted(true)
        if (
            variant === TextVariants.newlyadded &&
            value &&
            value !== getPropertyDefaultValue() &&
            value !== articleDefaultValue
        ) {
            ;((inputRef?.current as unknown) as HTMLInputElement).value = value
            setHasChanges(true)
        }
        return () => {
            setIsMounted(false)
        }
    }, [])

    React.useEffect(() => {
        // add or remove refs
        if (variant === TextVariants.newPill && !!fieldState?.elements) {
            setUomInPillRefs(
                (uomInPillRefs) =>
                    fieldState?.elements?.map((elem, index) => {
                        if (elem.type === TextVariants.uomInPill) {
                            return uomInPillRefs[index] || React.createRef()
                        }
                    })!
            )
        }
    }, [fieldState])
    React.useEffect(() => {
        if (hasErrors && value.includes(':')) {
            text.current = isElementDeleted
                ? value
                : variant === TextVariants.newlyadded
                ? value === getPropertyDefaultValue()
                    ? emptySpace
                    : encodeEntities(decodeEntities(value))
                : encodeEntities(decodeEntities(value))
            setHasErrors(false)
        }
    }, [hasErrors, value, text.current, isElementDeleted, variant, emptySpace])

    // TODO: narrow the RefObject types if possible
    React.useEffect(() => {
        if (
            isAddingFailed &&
            variant === TextVariants.newlyadded &&
            isMounted
        ) {
            ;((inputRef?.current as unknown) as HTMLInputElement)?.focus()
        }
        if (variant === TextVariants.newReplacement) {
            ;((textRef?.current as unknown) as HTMLInputElement)?.focus()
        }
        if (variant === TextVariants.newUOM && focusState) {
            ;((uomRef?.current as unknown) as HTMLInputElement)?.focus()
        }
        if (variant === TextVariants.newPill) {
            if (!!fieldState?.elements && fieldState?.elements.length === 0) {
                ;((pillRef?.current as unknown) as HTMLInputElement)?.focus()
            } else if (fieldState?.elements) {
                const index = fieldState?.elements?.length - 2

                ;((uomInPillRefs[index]
                    ?.current as unknown) as HTMLInputElement)?.focus()
            }
        }
    }, [
        isAddingFailed,
        variant,
        isMounted,
        focusState,
        uomInPillRefs,
        fieldState?.elements,
    ])

    React.useEffect(() => {
        if (value !== text.current || isToUpdateElement) {
            const textChanged = value !== text.current
            text.current = value
            if (textChanged) forceTextUpdate()
        }
    }, [value, isElementDeleted, isToUpdateElement])

    const handleRightClick = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            event.preventDefault()
            if (isBestPracticeEditDisabled || !elementId) return
            if (event.type === 'contextmenu') {
                onRightClick?.(
                    event,
                    template?.contentItem?.id,
                    isBestPractice!,
                    isGlobalBestPractice!
                )
            }
        },
        [
            onRightClick,
            elementId,
            isBestPractice,
            isGlobalBestPractice,
            isBestPracticeEditDisabled,
        ]
    )

    const registerKeyPress = useCallback(
        (e) => {
            const activeElementId = (document.activeElement as HTMLElement).id
            const elementIdsArray = [
                'spanElementText',
                'inputElementText',
                'replacementOptionText',
            ]
            const { lastElementCursorIndexIds } = workspaceContents()
            if (
                (tryGetShortcutKey(ShortcutKeys.ArrowUp, e) ||
                    tryGetShortcutKey(ShortcutKeys.ArrowDown, e)) &&
                lastHoveredItem &&
                !(
                    activeElementId &&
                    elementIdsArray.some((id) => activeElementId.includes(id))
                )
            ) {
                haltEvent(e)
                updateLastElementIndexId({
                    ...lastElementCursorIndexIds,
                    [lastHoveredItem.id]: 1,
                })
                ;(lastHoveredItem as HTMLElement).focus()
            }
        },
        [lastHoveredItem, updateLastElementIndexId]
    )

    React.useEffect(() => {
        window.addEventListener('keydown', registerKeyPress)
        return () => {
            window.removeEventListener('keydown', registerKeyPress)
        }
    }, [registerKeyPress])

    /**
     * Handles the change of the pill rendering
     * UOM inside a pill, pill for Text
     */
    const renderPillInUOM = React.useCallback(
        (elements) => {
            return (
                <>
                    {elements.map((elem: any, index: number) => {
                        switch (elem.type) {
                            case TextVariants.uomInPill:
                                return (
                                    <>
                                        <ContentEditable
                                            id={`uomInPillImperial${elem.id}`}
                                            data-title={titleText}
                                            innerRef={uomInPillRefs[index]}
                                            className={clsx(
                                                classes.editableText,
                                                {
                                                    [classes.addedBoxedElement]:
                                                        isTrackChangeToggleOn &&
                                                        isElementAdded,
                                                    [classes.addedPillInUOM]: true,
                                                    [classes.editableTextBoxed]: true,
                                                    [classes.notEditableText]: !editable,
                                                    [classes.editableTextPlain]: true,
                                                    [classes.nonEmptyPlainTextEditor]:
                                                        text.current.length >
                                                            0 ||
                                                        isElementDeleted,
                                                    [classes.emptyPlainTextEditor]:
                                                        text.current.length <=
                                                            0 &&
                                                        !isElementDeleted,
                                                    [classes.borderLessHasError]: hasErrors,
                                                    [classes.addedElementText]:
                                                        isTrackChangeToggleOn &&
                                                        isElementAdded,
                                                    [classes.deletedText]:
                                                        isElementDeleted &&
                                                        isTrackChangeToggleOn,
                                                }
                                            )}
                                            html={elem.imperialUnits}
                                            tabIndex={0}
                                            onChange={(event) =>
                                                handleChange(
                                                    event,
                                                    'imperial',
                                                    index
                                                )
                                            }
                                        />
                                        <span>(</span>
                                        <ContentEditable
                                            id={`uomInPillMetric${elem.id}`}
                                            data-title={titleText}
                                            className={clsx(
                                                classes.editableText,
                                                {
                                                    [classes.addedBoxedElement]:
                                                        isTrackChangeToggleOn &&
                                                        isElementAdded,
                                                    [classes.addedPillInUOM]: true,
                                                    [classes.editableTextBoxed]: true,
                                                    [classes.notEditableText]: !editable,
                                                    [classes.editableTextPlain]: true,
                                                    [classes.nonEmptyPlainTextEditor]:
                                                        text.current.length >
                                                            0 ||
                                                        isElementDeleted,
                                                    [classes.emptyPlainTextEditor]:
                                                        text.current.length <=
                                                            0 &&
                                                        !isElementDeleted,
                                                    [classes.borderLessHasError]: hasErrors,
                                                    [classes.addedElementText]:
                                                        isTrackChangeToggleOn &&
                                                        isElementAdded,
                                                    [classes.deletedText]:
                                                        isElementDeleted &&
                                                        isTrackChangeToggleOn,
                                                }
                                            )}
                                            html={elem.metricUnits}
                                            tabIndex={0}
                                            onChange={(event) =>
                                                handleChange(
                                                    event,
                                                    'metric',
                                                    index
                                                )
                                            }
                                        />
                                        <span>)</span>
                                    </>
                                )
                            default:
                                return (
                                    <ContentEditable
                                        id={`uomInPillText${elem.id}`}
                                        data-title={titleText}
                                        className={clsx(classes.editableText, {
                                            [classes.notEditableText]: !editable,
                                            [classes.addedPillInUOMText]:
                                                elem?.text?.trim()?.length <= 2,
                                            [classes.editableTextPlain]: true,
                                            [classes.nonEmptyPlainTextEditor]:
                                                text.current.length > 0 ||
                                                isElementDeleted,
                                            [classes.emptyPlainTextEditor]:
                                                text.current.length <= 0 &&
                                                !isElementDeleted,
                                            [classes.borderLessHasError]: hasErrors,
                                            [classes.addedElementText]:
                                                isTrackChangeToggleOn &&
                                                isElementAdded,
                                            [classes.deletedText]:
                                                isElementDeleted &&
                                                isTrackChangeToggleOn,
                                        })}
                                        html={elem.text ?? ''}
                                        tabIndex={0}
                                        onChange={(event) =>
                                            handleChange(event, 'text', index)
                                        }
                                    />
                                )
                        }
                    })}
                </>
            )
        },
        [fieldState, onChange, handleChange, handleKeyDown, element, template]
    )

    const strikeOut = React.useCallback(() => {
        if (boxedElementRef.current) {
            const elemHeight = boxedElementRef.current.clientHeight
            const lines = Math.floor(elemHeight / 13) // 13 is the calculated height for linebreak detection
            const width =
                Number(boxedElementRef.current?.clientWidth) +
                (isDeleteIconVisible && !isElementDeleted ? -20 : 20)
            return { width, lines }
        }
    }, [boxedElementRef, isDeleteIconVisible, isElementDeleted])

    const renderStrikeout = React.useCallback(() => {
        return (
            !!strikeOut()?.lines &&
            Array(strikeOut()?.lines)
                .fill('')
                .map((_, index) => (
                    <span
                        key={index}
                        className={classes.strikeout}
                        style={{
                            width: `${strikeOut()?.width}px`,
                            top: pxToRem((index + 1) * 7 + 3 * index),
                        }}
                    />
                ))
        )
    }, [strikeOut, isDeleteIconVisible, isElementDeleted])

    const placeholderText = React.useMemo(() => {
        return isArticleText
            ? articleDefaultValue
            : value === 'Add your value here.'
            ? value
            : getPropertyDefaultValue()
    }, [isArticleText, articleDefaultValue, value, getPropertyDefaultValue])
    return (
        <>
            {variant === TextVariants.newlyadded ? (
                <Input
                    data-testid="newlyaddedTextBox"
                    {...rest}
                    disableUnderline
                    id={identifier}
                    multiline
                    placeholder={placeholderText}
                    autoFocus
                    onFocusCapture={handlePillRequirements}
                    onClick={handlePillRequirements}
                    onKeyDown={handleAddKeyDown}
                    onKeyUp={handleInputKeyUp}
                    onPaste={handlePaste}
                    onChange={handleChange}
                    inputRef={inputRef}
                    classes={{
                        input: clsx(
                            classes.editableText,
                            {
                                [classes.editableNewlyAdded]: true,
                                [classes.nonEmptyPlainTextEditor]:
                                    text.current.length > 0,
                                [classes.emptyPlainTextEditor]:
                                    text.current.length <= 0,
                                [classes.borderLessHasError]: hasErrors,
                                [classes.articleText]: isArticleText,
                            },
                            isTrackChangeToggleOn &&
                                isElementAdded &&
                                classes.addedElementText
                        ),
                        root: classes.newlyAddedContainer,
                    }}
                    onBlur={debounce(handleBlur, 500)}
                ></Input>
            ) : (
                <span
                    {...rest}
                    data-title={titleText}
                    className={clsx(
                        classes.hasTitle,
                        variant === TextVariants.plain &&
                            (nonEditableFocusableArticle ||
                                productTypeInFamilyViewContentType) &&
                            classes.ptGroupText,
                        nonEditableFocusableArticle &&
                            classes.nonEditableFocusableArticle
                    )}
                    onContextMenu={handleRightClick}
                    data-testid={'focusable-' + identifier}
                >
                    {variant === TextVariants.plain && (
                        <ContentEditable
                            disabled={isContentEditingDisabled}
                            innerRef={editorRef as React.RefObject<HTMLElement>}
                            id={
                                !(isElementDeleted && isTrackChangeToggleOn)
                                    ? identifier
                                    : 'deletedText'
                            }
                            data-title={titleText}
                            className={clsx(classes.editableText, {
                                [classes.notEditableText]: !editable,
                                [classes.editableTextPlain]: true,
                                [classes.nonEmptyPlainTextEditor]:
                                    text.current.length > 0 || isElementDeleted,
                                [classes.emptyPlainTextEditor]:
                                    text.current.length <= 0 &&
                                    !isElementDeleted,
                                [classes.borderLessHasError]: hasErrors,
                                [classes.addedElementText]:
                                    isTrackChangeToggleOn && isElementAdded,
                                [classes.deletedText]:
                                    isElementDeleted && isTrackChangeToggleOn,
                                [classes.articleText]: isArticleText,
                            })}
                            onFocus={handleArticleFocus}
                            onBlur={handleBlurFamilyTitle}
                            html={
                                isElementDeleted && isTrackChangeToggleOn
                                    ? value
                                    : text.current ?? ''
                            }
                            onKeyDown={handleKeyDown}
                            onClick={handlePillRequirements}
                            onKeyUp={(e) => {
                                haltEvent(e)
                            }}
                            onPaste={handlePaste}
                            tabIndex={0}
                            onChange={handleChange}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        />
                    )}
                    {(variant === TextVariants.boxed ||
                        variant === TextVariants.newReplacement ||
                        variant === TextVariants.newUOM) && (
                        <>
                            <span
                                data-testid="editableTextSpan"
                                className={clsx(classes.editableText, {
                                    [classes.editableTextBoxed]: true,
                                    [classes.addedBoxedElement]:
                                        isTrackChangeToggleOn && isElementAdded,
                                    [classes.deletedBoxedElement]:
                                        isTrackChangeToggleOn &&
                                        isElementDeleted,
                                    [classes.hasError]: editable && hasErrors,
                                    [classes.hasNoErrors]:
                                        editable && !hasErrors,
                                    [classes.emptyEditableTextBox]:
                                        value.length === 0,
                                    [classes.disabledBoxedInputContainer]:
                                        !editable && !calledFromImportPage,
                                    [classes.notDisabledBoxedInputContainer]:
                                        (editable || calledFromImportPage) &&
                                        !element?.isPremiumPlus &&
                                        !isElementDeleted,
                                    [classes.manufacturerUnits]:
                                        !!element?.isPremiumPlus && !hasErrors,
                                })}
                            >
                                <div
                                    ref={
                                        boxedElementRef as React.RefObject<
                                            HTMLDivElement
                                        >
                                    }
                                    className={
                                        classes.nonEmptyPlainTextEditorContainer
                                    }
                                >
                                    <ContentEditable
                                        disabled={
                                            !editable ||
                                            (isElementDeleted &&
                                                isTrackChangeToggleOn) ||
                                            (fieldStateType === // In SMM we don't allow user to edit a already created ReplacementOption
                                                'ReplacementOption' &&
                                                contentTeamFromStudioPage)
                                        }
                                        innerRef={
                                            variant ===
                                            TextVariants.newReplacement
                                                ? textRef
                                                : variant ===
                                                  TextVariants.newUOM
                                                ? uomRef
                                                : (editorRef as React.RefObject<
                                                      HTMLElement
                                                  >)
                                        }
                                        id={identifier}
                                        data-title={titleText}
                                        className={clsx(
                                            classes.actualBoxedEditor,
                                            {
                                                [classes.nonEmptyActualBoxedEditor]:
                                                    value.length > 0,
                                                [classes.emptyEditableTextBox]:
                                                    value.length === 0,
                                                [classes.deletedBoxedText]:
                                                    isElementDeleted &&
                                                    isTrackChangeToggleOn,
                                            }
                                        )}
                                        html={
                                            hideCustomManufacturer ||
                                            value ===
                                                'Insert Manufacturers Name'
                                                ? 'Insert Manufacturers Name'
                                                : text.current ?? ''
                                        }
                                        onKeyDown={handleKeyDown}
                                        onClick={handlePillRequirements}
                                        onPaste={handlePaste}
                                        tabIndex={0}
                                        onChange={handleChange}
                                    />
                                    {isDeleteIconVisible && !isElementDeleted && (
                                        <span className={classes.endIcon}>
                                            <DeletePills
                                                isInsertText={true}
                                                onDeleteOption={onDeleteOption}
                                                htmlColor={
                                                    isTrackChangeToggleOn &&
                                                    isElementAdded
                                                        ? '#FF6060'
                                                        : '#000000'
                                                }
                                            />
                                        </span>
                                    )}
                                    {isElementDeleted &&
                                        isTrackChangeToggleOn &&
                                        renderStrikeout()}
                                </div>
                            </span>
                        </>
                    )}

                    {variant === TextVariants.newPill &&
                        !!fieldState?.elements &&
                        fieldState?.elements.length === 0 && (
                            <div
                                id="pillCreation"
                                data-testid="pillCreation"
                                className={classes.unselectedOptionItem}
                            >
                                <ContentEditable
                                    data-title={titleText}
                                    onChange={handleChange}
                                    innerRef={pillRef}
                                    id="pillCreation"
                                    data-testid="pillCreation"
                                    onKeyDown={
                                        handleKeyDown as React.KeyboardEventHandler<
                                            HTMLDivElement
                                        >
                                    }
                                    onClick={handlePillRequirements}
                                    onBlur={
                                        handleBlur as
                                            | React.FocusEventHandler<
                                                  HTMLDivElement
                                              >
                                            | undefined
                                    }
                                    className={clsx(classes.editableText, {
                                        [classes.notEditableText]: !editable,

                                        [classes.editableTextPlain]: true,
                                        [classes.nonEmptyPlainTextEditor]:
                                            text.current.length > 0 ||
                                            isElementDeleted,
                                        [classes.emptyPlainTextEditor]:
                                            text.current.length <= 0 &&
                                            !isElementDeleted,
                                        [classes.borderLessHasError]: hasErrors,
                                        [classes.addedElementText]:
                                            isTrackChangeToggleOn &&
                                            isElementAdded,
                                        [classes.deletedText]:
                                            isElementDeleted &&
                                            isTrackChangeToggleOn,
                                    })}
                                    html={text.current ?? ''}
                                />
                            </div>
                        )}
                    {variant === TextVariants.newPill &&
                        !!fieldState?.elements &&
                        fieldState?.elements.length > 0 && (
                            <>
                                <div
                                    ref={
                                        boxedUomInPillRef as React.RefObject<
                                            HTMLDivElement
                                        >
                                    }
                                    className={classes.unselectedOptionItempill}
                                    id={TextVariants.uomInPill}
                                    onBlur={
                                        handleBlur as
                                            | React.FocusEventHandler<
                                                  HTMLDivElement
                                              >
                                            | undefined
                                    }
                                    onKeyDown={
                                        handleKeyDown as
                                            | React.KeyboardEventHandler<
                                                  HTMLDivElement
                                              >
                                            | undefined
                                    }
                                    onClick={handlePillRequirements}
                                >
                                    {renderPillInUOM(fieldState.elements)}
                                </div>
                            </>
                        )}
                </span>
            )}
            {contentTeamFromStudioPage &&
                isContentTeamUser &&
                isPublishingAuthor &&
                !selectedProjectNode?.isSpecTextNode &&
                variant === TextVariants.newlyadded &&
                (template?.partType === 'Products' ||
                    parentTemplate?.partType === 'Products') &&
                !inputHasSomeValue &&
                !parentTemplate?.isProductType &&
                (parentTemplate?.isProduct ||
                    ((parentTemplate?.type == 'Nugget' ||
                        parentTemplate?.type == 'ProductTypeProperty') &&
                        parentTemplate?.contentItem?.textType ==
                            'Article')) && (
                    <AddProductType
                        data-testid="product-icon"
                        template={template}
                        parent={parentTemplate}
                        resetInput={resetText}
                    />
                )}

            {contentTeamFromStudioPage &&
                isContentTeamUser &&
                !isSustainabilityTopic &&
                isPublishingAuthor &&
                variant === 'newlyadded' &&
                !inputHasSomeValue &&
                !isArticleText && (
                    <AddSustainabilityTopic
                        data-testid="sustainability-icon"
                        template={template}
                        parent={parentTemplate}
                        resetInput={resetSustainabilityText}
                    />
                )}
        </>
    )
}

export default PropertyEditorEditableText
